// db.js — Supabase data layer for Nurse CNE
// Plain JS (not JSX). Exposes window.DB and window.AuthAPI.
// All functions return Promises and never throw — they resolve to
// { data, error } or a plain value on success.

(function () {
  const sb = window.sb;
  if (!sb) { console.error('db.js: window.sb missing — supabase client not initialised'); return; }

  // Tracks which sign-in path the user last used so the SignInScreen can
  // surface a "Last used" pill next to that option on the next launch.
  // Stored in localStorage (no secret material — just the string 'email'
  // or 'apple'). Never cleared on sign-out: the badge survives so the
  // returning user sees the right hint next time.
  const LAST_LOGIN_KEY = 'vital_last_login_method';

  // Google Web Client ID for Google Sign-In. Used by @capawesome/capacitor-google-sign-in
  // on all platforms (it is the server/client ID passed to the native SDKs) and by the
  // web OAuth fallback branch. The iOS OAuth client ID is configured in Info.plist.
  const GOOGLE_WEB_CLIENT_ID = '881313955356-dqna7d2hbcm16c9e02m6rjaf60o92hhf.apps.googleusercontent.com';

  // One-time GoogleSignIn plugin initialization promise. The plugin requires initialize()
  // before signIn(); we memoize it so re-auth and subsequent sign-ins reuse the same call.
  let googleInitPromise = null;

  function markLastLogin(method) {
    try { localStorage.setItem(LAST_LOGIN_KEY, method); } catch (_) {}
  }

  // Remembered identity for the returning-user quick-login card on SignInScreen.
  // UNLIKE onboardedKey/skipKey below (which deliberately store NO PII — see
  // their comment), this intentionally caches the last email-signed-in user's
  // email + display name so the sign-in screen can greet them and prefill the
  // email field. That is the feature; it is consistent with the app already
  // persisting the Supabase session, and the user can wipe it via "Use a
  // different account" on the card (clearLastIdentity) or by deleting their
  // account. Never cleared on plain sign-out — the returning user should still
  // see the card next launch (same lifetime as LAST_LOGIN_KEY above).
  const LAST_IDENTITY_KEY = 'vital_last_identity_v1';
  function getLastIdentity() {
    try { return JSON.parse(localStorage.getItem(LAST_IDENTITY_KEY)) || null; }
    catch (_) { return null; }
  }
  // Merge-by-email: an email-only write (from signIn, before the profile has
  // loaded) must NOT clobber a display name already cached for the SAME user,
  // and a name must NEVER be carried across onto a different email. localStorage
  // read-modify-write is synchronous within the event loop, so callback ORDERING
  // is the only concern (signIn's email-only write vs. getProfile's name write,
  // in either order) — this merge resolves both orderings deterministically.
  function rememberIdentity(next) {
    if (!next || !next.email) return;
    try {
      const prev = getLastIdentity();
      const sameUser = prev && prev.email === next.email;
      const name = (next.name != null && String(next.name).trim() !== '')
        ? String(next.name).trim()
        : (sameUser ? (prev.name || null) : null);
      localStorage.setItem(LAST_IDENTITY_KEY, JSON.stringify({ email: next.email, name }));
    } catch (_) {}
  }
  function clearLastIdentity() {
    try { localStorage.removeItem(LAST_IDENTITY_KEY); } catch (_) {}
  }

  // Per-user onboarded flag — used to decide where to route on cold
  // start when the profile fetch fails (e.g. Airplane Mode, captive
  // portal). We only cache the BOOLEAN, never the profile body itself,
  // so this never contains PII (name, license number, hospital). Key
  // is scoped to userId so a different account on the same device gets
  // its own answer. Cleared in AuthAPI.deleteAccount.
  function onboardedKey(userId) { return `vital_onboarded_${userId}`; }
  function markUserOnboarded(userId) {
    if (!userId) return;
    try { localStorage.setItem(onboardedKey(userId), '1'); } catch (_) {}
  }
  function wasUserOnboarded(userId) {
    if (!userId) return false;
    try { return localStorage.getItem(onboardedKey(userId)) === '1'; }
    catch (_) { return false; }
  }
  function clearUserOnboarded(userId) {
    if (!userId) return;
    try { localStorage.removeItem(onboardedKey(userId)); } catch (_) {}
  }

  // Per-user "I'd rather fill the profile later" flag — set when the
  // user taps Skip on the onboarding Welcome step. Bootstrap routing
  // treats a skipped user as 'ready' (lets them into the app) even
  // though profile.onboarded is still false. Re-entering the
  // OnboardingFlow from Profile and completing it clears the flag
  // (markUserOnboarded) so the two never disagree.
  function skipKey(userId) { return `vital_onboard_skipped_${userId}`; }
  function markOnboardingSkipped(userId) {
    if (!userId) return;
    try { localStorage.setItem(skipKey(userId), '1'); } catch (_) {}
  }
  function wasOnboardingSkipped(userId) {
    if (!userId) return false;
    try { return localStorage.getItem(skipKey(userId)) === '1'; }
    catch (_) { return false; }
  }
  function clearOnboardingSkipped(userId) {
    if (!userId) return;
    try { localStorage.removeItem(skipKey(userId)); } catch (_) {}
  }

  // Per-user "we've already shown the push soft-ask" flag. The pre-permission
  // priming sheet is offered AT MOST ONCE per user (after their first value
  // moment — saving a course or logging a record), never at cold start. We
  // record that we showed it the moment it opens so a "Not now" never nags
  // again; the user can still enable push later from Notification settings.
  // Boolean-only, scoped to userId, no PII — same rationale as onboardedKey.
  function pushSoftAskKey(userId) { return `vital_push_softask_${userId}`; }
  function markPushSoftAskShown(userId) {
    if (!userId) return;
    try { localStorage.setItem(pushSoftAskKey(userId), '1'); } catch (_) {}
  }
  function wasPushSoftAskShown(userId) {
    if (!userId) return false;
    try { return localStorage.getItem(pushSoftAskKey(userId)) === '1'; }
    catch (_) { return false; }
  }

  // Per-user App Store review-prompt cooldown (epoch-ms of the last time we
  // ASKED). Apple's system prompt is itself capped at 3×/365 days and silently
  // no-ops past the cap — but we must NOT lean on that, because a swallowed
  // prompt still "spends" a value moment for nothing. So we record when we last
  // asked and enforce our own cooldown on top (see app.jsx maybeOfferReview).
  // A timestamp, not a boolean, so the cooldown can lapse and re-arm. Scoped to
  // userId, no PII — same rationale as pushSoftAskKey.
  function reviewPromptedKey(userId) { return `vital_review_prompted_${userId}`; }
  function markReviewPrompted(userId, ts) {
    if (!userId) return;
    try { localStorage.setItem(reviewPromptedKey(userId), String(ts ?? Date.now())); } catch (_) {}
  }
  function lastReviewPromptedAt(userId) {
    if (!userId) return 0;
    try { return parseInt(localStorage.getItem(reviewPromptedKey(userId)) || '0', 10) || 0; }
    catch (_) { return 0; }
  }

  // Per-user "explicitly turned push OFF in-app" flag. Distinguishes "this
  // account chose to disable push" from "this account just never enabled it"
  // (both look like 'no device_tokens row'). ensureRegistered() consults it so
  // the silent re-register on login NEVER overrides a deliberate in-app off,
  // while a fresh account on an already-granted device still gets provisioned.
  // Set when the master toggle goes off; cleared when it goes on.
  function pushOptOutKey(userId) { return `vital_push_optout_${userId}`; }
  function markPushOptedOut(userId) {
    if (!userId) return;
    try { localStorage.setItem(pushOptOutKey(userId), '1'); } catch (_) {}
  }
  function clearPushOptedOut(userId) {
    if (!userId) return;
    try { localStorage.removeItem(pushOptOutKey(userId)); } catch (_) {}
  }
  function wasPushOptedOut(userId) {
    if (!userId) return false;
    try { return localStorage.getItem(pushOptOutKey(userId)) === '1'; }
    catch (_) { return false; }
  }

  // ── AUTH ──────────────────────────────────────────────────────────────────
  const AuthAPI = {
    async signUp(email, password) {
      // emailRedirectTo MUST be passed explicitly. Without it Supabase falls
      // back to the project's Site URL — and if that URL (or the user's
      // current origin) isn't on the redirect allowlist, the confirmation
      // link lands on `{"error":"requested path is invalid"}`. On Capacitor
      // native we route through the vital:// scheme so the link reopens the
      // app; on web we send the user back to the same origin/path they
      // signed up from. Both must be added to Supabase →
      // Authentication → URL Configuration → Redirect URLs.
      const isNative = !!globalThis.Capacitor?.isNativePlatform?.();
      const emailRedirectTo = isNative
        ? 'vital://signup'
        : window.location.origin + window.location.pathname;
      const { data, error } = await sb.auth.signUp({
        email,
        password,
        options: { emailRedirectTo },
      });
      if (!error && data?.session) { markLastLogin('email'); rememberIdentity({ email }); }
      return { data, error };
    },
    async signIn(email, password) {
      const { data, error } = await sb.auth.signInWithPassword({ email, password });
      if (!error && data?.session) { markLastLogin('email'); rememberIdentity({ email }); }
      return { data, error };
    },
    getLastLoginMethod() {
      try { return localStorage.getItem(LAST_LOGIN_KEY); } catch (_) { return null; }
    },
    getLastIdentity,
    rememberIdentity,
    clearLastIdentity,
    markUserOnboarded,
    wasUserOnboarded,
    clearUserOnboarded,
    markOnboardingSkipped,
    wasOnboardingSkipped,
    clearOnboardingSkipped,

    // Sign in with Apple — supports both clients via one entry point:
    //
    //   iOS native (Capacitor):
    //     1. Generate { raw, hashed } nonce pair.
    //     2. Pass HASHED to @capacitor-community/apple-sign-in. Apple
    //        embeds that exact hashed string in the identityToken's
    //        `nonce` claim.
    //     3. Pass identityToken + RAW nonce to sb.auth.signInWithIdToken.
    //        Supabase computes SHA-256(raw) and compares it to the JWT's
    //        nonce claim. Match ⇒ session.
    //     Mixing raw/hashed across these calls produces an
    //     "invalid nonce" / token verification failure from Supabase.
    //
    //   Web (browser):
    //     sb.auth.signInWithOAuth({ provider: 'apple' }) — Supabase
    //     redirects to Apple's web sign-in flow and back via the
    //     callback URL configured in the Apple provider settings.
    //     The web button isn't wired in v1 (per Step 7 scope) but the
    //     branch exists here so it works the moment a button is added.
    //
    // Returns { data, error } shape matching the other AuthAPI methods.
    async signInWithApple() {
      const isNative = !!globalThis.Capacitor?.isNativePlatform?.();
      console.log('[Vital SIWA] platform branch:', isNative ? 'native' : 'web');

      if (!isNative) {
        // Mark BEFORE the redirect leaves the page — signInWithOAuth
        // navigates away, so the post-callback success doesn't have a
        // clean hook to call markLastLogin from. Marking on intent is
        // close enough for the "Last used" badge.
        markLastLogin('apple');
        const { data, error } = await sb.auth.signInWithOAuth({
          provider: 'apple',
          options: { redirectTo: window.location.origin + window.location.pathname },
        });
        if (error) console.warn('[Vital SIWA] web OAuth start failed:', error.message);
        if (!error && data?.url) window.location.href = data.url;
        return { data, error };
      }

      const CapAppleSignIn = window.CapacitorAppleSignIn;
      const genNonce = window.generateAppleNoncePair;
      if (!CapAppleSignIn || typeof genNonce !== 'function') {
        const msg = 'Sign in with Apple is not available on this device.';
        console.warn('[Vital SIWA] plugin or nonce helper missing on window');
        return { data: null, error: { message: msg } };
      }

      let raw, hashed;
      try {
        ({ raw, hashed } = await genNonce());
        console.log('[Vital SIWA] nonce generated (raw length:', raw.length,
          ', hashed length:', hashed.length, ')');
      } catch (e) {
        console.warn('[Vital SIWA] nonce generation failed', e);
        return { data: null, error: { message: 'Could not generate nonce' } };
      }

      let appleResult;
      try {
        appleResult = await CapAppleSignIn.authorize({
          // iOS bundle ID. Must match the App ID with SIWA capability
          // enabled in the Apple Developer Portal.
          clientId: 'hk.vital.cne',
          // Apple requires a value here; iOS native flow doesn't actually
          // use it. We supply the app's URL scheme for completeness.
          redirectURI: 'vital://siwa',
          scopes: 'email name',
          state: '',
          nonce: hashed,
        });
      } catch (e) {
        // User cancelled the sheet, or plugin error. Treat both as a
        // soft cancel — let the caller stay on the sign-in screen.
        console.warn('[Vital SIWA] authorize threw:', e?.message || e);
        return { data: null, error: { message: e?.message || 'Sign in with Apple was cancelled' } };
      }

      const identityToken = appleResult?.response?.identityToken;
      if (!identityToken) {
        console.warn('[Vital SIWA] no identityToken in plugin response');
        return { data: null, error: { message: 'Apple did not return an identity token' } };
      }
      console.log('[Vital SIWA] identityToken received (length:', identityToken.length,
        ', prefix:', identityToken.slice(0, 6) + '…)');

      // Apple returns givenName / familyName / email ONLY on the very first
      // authorization for a given Apple ID + bundle ID pair. On every
      // subsequent sign-in (and on a re-auth after the user revokes Vital
      // in iOS Settings → Apple ID → Sign in with Apple → Apps using
      // Apple ID → Stop using → re-authorise), these fields come back as
      // null even though the user clearly has a name in their Apple
      // profile. We capture them here for the SIWA-bootstrap step below.
      const appleGivenName  = (appleResult?.response?.givenName  || '').trim();
      const appleFamilyName = (appleResult?.response?.familyName || '').trim();
      const appleFullName   = [appleGivenName, appleFamilyName].filter(Boolean).join(' ').trim();

      const { data, error } = await sb.auth.signInWithIdToken({
        provider: 'apple',
        token: identityToken,
        nonce: raw,
      });
      if (error) {
        console.warn('[Vital SIWA] supabase signInWithIdToken failed:', error.message);
        return { data, error };
      }
      console.log('[Vital SIWA] supabase sign-in ok; user id =', data?.user?.id);
      markLastLogin('apple');

      // ── SIWA UX policy (App Store Review compliance) ───────────────────
      // Apple Review explicitly forbids forcing a SIWA user to re-enter
      // name / email after authorisation, because Authentication Services
      // already provided that data. We satisfy this in two steps:
      //
      //   (1) Mark onboarding as "skipped" for this user. app.jsx's
      //       finalize() routes a session with wasOnboardingSkipped=true
      //       straight to 'ready' (home) instead of 'onboarding'. The
      //       optional Hong Kong nurse fields (license number, hospital,
      //       cycle dates) can be filled later via Profile → Complete
      //       profile — the entry point already exists and remains
      //       discoverable from the profile screen.
      //
      //       This MUST be a synchronous localStorage write so it lands
      //       before the supabase onAuthStateChange listener (which fires
      //       during signInWithIdToken) reaches its wasOnboardingSkipped
      //       check. The listener's first await is DB.getProfile (a
      //       network round-trip), so we have plenty of time.
      //
      //   (2) Best-effort silently persist the Apple-provided name to
      //       public.profiles, but ONLY when the existing row has no
      //       name yet. If the user previously typed a different name
      //       (e.g. completed onboarding with their preferred Chinese
      //       name) and later revoked SIWA in iOS Settings and re-signed
      //       in, Apple will provide givenName / familyName again — we
      //       must NOT clobber the user's chosen name with Apple's.
      //
      // Both steps are wrapped in their own try/catch and never block the
      // sign-in resolution: a failure here just leaves the user with a
      // bare profile row, which the rest of the app already handles
      // (see userFromProfile defaults in screens.jsx).
      try {
        const userId = data?.user?.id;
        if (userId) {
          markOnboardingSkipped(userId);

          if (appleFullName) {
            // Read first; only write if name field is still empty. This
            // avoids the revoke-and-resign race where Apple supplies its
            // canonical name on top of a user-chosen one.
            const existing = await getProfile(userId).catch(() => null);
            const hasName = !!(existing && (
              (existing.display_name && existing.display_name.trim()) ||
              (existing.name_en && existing.name_en.trim()) ||
              (existing.name_zh && existing.name_zh.trim())
            ));
            if (!hasName) {
              const shortName = appleGivenName || appleFullName.split(' ')[0] || appleFullName;
              await upsertProfile(userId, {
                display_name: appleFullName,
                name_en: appleFullName,
                short_name_en: shortName,
              }).catch((e) => {
                console.warn('[Vital SIWA] silent name persist failed', e?.message || e);
              });
              console.log('[Vital SIWA] persisted Apple name to profile:', appleFullName);
            } else {
              console.log('[Vital SIWA] profile already has a name — skipping Apple name overwrite');
            }
          }
        }
      } catch (e) {
        console.warn('[Vital SIWA] post-sign-in profile bootstrap failed', e?.message || e);
      }

      return { data, error: null };
    },

    // Sign in with Google — supports both web and native iOS:
    //
    //   Web (browser):
    //     sb.auth.signInWithOAuth({ provider: 'google' }) — same model as
    //     Apple web sign-in. The redirect URL is the current origin/path so
    //     it works on both localhost and production.
    //
    //   iOS native (Capacitor + @capawesome/capacitor-google-sign-in):
    //     1. Initialize the plugin with the Web Client ID (the plugin uses it
    //        as the server client ID on iOS).
    //     2. Call signIn() to get { idToken, accessToken, email, displayName,
    //        familyName, givenName } from the Google Sign-In SDK.
    //     3. Pass idToken (and accessToken if available, needed for at_hash
    //        verification) to sb.auth.signInWithIdToken.
    //     4. Mark onboarding skipped and silently persist the Google-provided
    //        name to public.profiles if the row has no name yet.
    //
    // Returns { data, error } shape matching the other AuthAPI methods.
    async signInWithGoogle() {
      const isNative = !!globalThis.Capacitor?.isNativePlatform?.();
      console.log('[Vital Google] platform branch:', isNative ? 'native' : 'web');

      if (!isNative) {
        markLastLogin('google');
        const { data, error } = await sb.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: window.location.origin + window.location.pathname,
          },
        });
        if (error) console.warn('[Vital Google] web OAuth start failed:', error.message);
        if (!error && data?.url) window.location.href = data.url;
        return { data, error };
      }

      const CapGoogleSignIn = window.CapGoogleSignIn;
      if (!CapGoogleSignIn) {
        const msg = 'Google Sign-In is not available on this device.';
        console.warn('[Vital Google] plugin missing on window');
        return { data: null, error: { message: msg } };
      }

      try {
        if (!googleInitPromise) {
          googleInitPromise = CapGoogleSignIn.initialize({
            clientId: GOOGLE_WEB_CLIENT_ID,
            scopes: [
              'https://www.googleapis.com/auth/userinfo.profile',
              'https://www.googleapis.com/auth/userinfo.email',
            ],
          });
        }
        await googleInitPromise;
      } catch (e) {
        console.warn('[Vital Google] initialize failed:', e?.message || e);
        return { data: null, error: { message: 'Google Sign-In could not be initialized' } };
      }

      let googleResult;
      try {
        googleResult = await CapGoogleSignIn.signIn();
      } catch (e) {
        console.warn('[Vital Google] signIn threw:', e?.message || e);
        return { data: null, error: { message: e?.message || 'Google Sign-In was cancelled' } };
      }

      const idToken = googleResult?.idToken;
      if (!idToken) {
        console.warn('[Vital Google] no idToken in plugin response');
        return { data: null, error: { message: 'Google did not return an ID token' } };
      }
      console.log('[Vital Google] idToken received (length:', idToken.length,
        ', prefix:', idToken.slice(0, 6) + '…)');

      const accessToken = googleResult?.accessToken || null;
      const googleEmail = (googleResult?.email || '').trim();
      const googleDisplayName = (googleResult?.displayName || '').trim();
      const googleGivenName = (googleResult?.givenName || '').trim();
      const googleFamilyName = (googleResult?.familyName || '').trim();
      const googleFullName = googleDisplayName ||
        [googleGivenName, googleFamilyName].filter(Boolean).join(' ').trim() ||
        googleEmail;

      const signInArgs = { provider: 'google', token: idToken };
      if (accessToken) signInArgs.access_token = accessToken;
      const { data, error } = await sb.auth.signInWithIdToken(signInArgs);
      if (error) {
        console.warn('[Vital Google] supabase signInWithIdToken failed:', error.message);
        return { data, error };
      }
      console.log('[Vital Google] supabase sign-in ok; user id =', data?.user?.id);
      markLastLogin('google');

      // Same UX policy as Apple: a first-party sign-in provider has already
      // supplied name/email, so we skip the onboarding name step and silently
      // persist the Google name if the profile row has no name yet.
      try {
        const userId = data?.user?.id;
        if (userId) {
          markOnboardingSkipped(userId);

          if (googleFullName) {
            const existing = await getProfile(userId).catch(() => null);
            const hasName = !!(existing && (
              (existing.display_name && existing.display_name.trim()) ||
              (existing.name_en && existing.name_en.trim()) ||
              (existing.name_zh && existing.name_zh.trim())
            ));
            if (!hasName) {
              const shortName = googleGivenName || googleDisplayName.split(' ')[0] || googleDisplayName;
              await upsertProfile(userId, {
                display_name: googleDisplayName,
                name_en: googleDisplayName,
                short_name_en: shortName,
              }).catch((e) => {
                console.warn('[Vital Google] silent name persist failed', e?.message || e);
              });
              console.log('[Vital Google] persisted Google name to profile:', googleDisplayName);
            } else {
              console.log('[Vital Google] profile already has a name — skipping Google name overwrite');
            }
          }
        }
      } catch (e) {
        console.warn('[Vital Google] post-sign-in profile bootstrap failed', e?.message || e);
      }

      return { data, error: null };
    },

    async signOut() { return sb.auth.signOut(); },
    async resetPassword(email) {
      // On Capacitor native, route the recovery email back through the
      // vital:// custom URL scheme so iOS opens the app instead of Safari.
      // The deep-link listener in app.jsx parses the recovery tokens out
      // of the URL hash and calls sb.auth.setSession({...}) manually,
      // because detectSessionInUrl is OFF on native (the WebView never
      // receives the redirect URL the way a browser tab would).
      const isNative = !!globalThis.Capacitor?.isNativePlatform?.();
      const redirectTo = isNative
        ? 'vital://reset'
        : window.location.origin + window.location.pathname;
      const { data, error } = await sb.auth.resetPasswordForEmail(email, { redirectTo });
      return { data, error };
    },
    async getSession() { const { data } = await sb.auth.getSession(); return data.session; },
    // Returns 'email' | 'apple' | string | null — the provider the user
    // should be re-challenged with for sensitive operations (currently
    // account deletion).
    //
    // We CANNOT rely on `app_metadata.provider` alone: that field tracks
    // the ORIGINAL sign-up provider, not the most recent sign-in. A user
    // who first registered with email and later linked Apple (or whose
    // Apple identity was auto-linked by Supabase because the Apple email
    // matched an existing email row) would show provider='email' even
    // though they only ever sign in with Apple — and our deletion sheet
    // would prompt for a password they don't have.
    //
    // Priority order:
    //   1. If any `user.identities[*].provider === 'apple'`, require
    //      Apple re-auth.
    //   2. Else if any `user.identities[*].provider === 'google'`, require
    //      Google re-auth. OAuth providers take precedence over email.
    //   3. Else fall back to `app_metadata.providers` or `app_metadata.provider`
    //      (typically 'email').
    //   4. Else null.
    getSessionProvider(session) {
      const user = session?.user;
      if (!user) return null;
      const identities = Array.isArray(user.identities) ? user.identities : [];
      if (identities.some((i) => i?.provider === 'apple')) return 'apple';
      if (identities.some((i) => i?.provider === 'google')) return 'google';
      const providers = user.app_metadata?.providers;
      if (Array.isArray(providers) && providers.includes('apple')) return 'apple';
      if (Array.isArray(providers) && providers.includes('google')) return 'google';
      return user.app_metadata?.provider || null;
    },
    // cb signature: (session, event) — event is the raw supabase-js event
    // string ('INITIAL_SESSION' | 'SIGNED_IN' | 'TOKEN_REFRESHED' |
    // 'SIGNED_OUT' | 'PASSWORD_RECOVERY' | 'USER_UPDATED'). Most callers
    // only care about session; the second arg is opt-in for paths that
    // need to distinguish a fresh sign-in from a cold-boot session
    // restore (e.g. the Apple re-auth resume hook for account deletion).
    onAuthChange(cb) {
      const { data } = sb.auth.onAuthStateChange((event, session) => cb(session, event));
      return () => data.subscription.unsubscribe();
    },

    // Permanent account deletion. Steps run in a strict order so a
    // partial failure can never leave Keychain / Preferences / push
    // tokens stranded for a deleted account.
    //
    //   1) Pre-server local cleanup — best-effort. Runs while the user's
    //      JWT still passes RLS, so the user can touch their own rows.
    //      Each plugin is feature-detected so this function ships
    //      forward-compatible with Steps 8-10 (biometric/push/prefs)
    //      that haven't been wired yet.
    //
    //   2) Edge Function delete-account — wipes every user-owned storage
    //      bucket (certificates + submission evidence), then
    //      auth.admin.deleteUser, which cascades to every public.* table.
    //      Returns { ok: true } only when all server steps succeed. On error
    //      we abort and leave local data intact so the user can retry without
    //      ending up partially deleted.
    //
    //   3) Post-server teardown — only runs after step 2 returns ok.
    //      Clears Keychain (biometric), removes user-scoped Preferences
    //      keys, cancels scheduled local notifications, then signs out
    //      (last so onAuthStateChange listeners don't fire mid-teardown).
    async deleteAccount() {
      const { data: { session } } = await sb.auth.getSession();
      if (!session) return { error: { message: 'Not signed in' } };
      const accessToken = session.access_token;

      // --- 1. Pre-server local cleanup (best-effort, feature-detected) ---
      try {
        if (window.PushNotifications?.unregister) {
          await window.PushNotifications.unregister().catch(() => {});
        }
      } catch (_) {}
      try {
        if (window.LocalNotifications?.getPending && window.LocalNotifications?.cancel) {
          const pending = await window.LocalNotifications.getPending().catch(() => null);
          if (pending?.notifications?.length) {
            await window.LocalNotifications.cancel({ notifications: pending.notifications }).catch(() => {});
          }
        }
      } catch (_) {}
      // device_tokens table doesn't exist yet (Step 10). When it lands,
      // delete the user's row here BEFORE the Edge Function — once
      // auth.admin.deleteUser runs, the cascade removes it anyway, but
      // doing it here keeps the order explicit if a future schema
      // change drops the FK cascade.

      // --- 2. Server-side cascade via Edge Function ---
      const url = `https://iyrxakaiowiilphxzyuq.supabase.co/functions/v1/delete-account`;
      let resp;
      try {
        resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: '{}',
        });
      } catch (e) {
        return { error: { message: `Network error: ${e?.message || e}` } };
      }
      let result = null;
      try { result = await resp.json(); } catch (_) { result = null; }
      if (!resp.ok || !result?.ok) {
        return { error: {
          message: result?.error || `Delete failed (HTTP ${resp.status})`,
          detail: result?.detail || null,
        } };
      }
      // Diagnostic log so a regression in storage cleanup surfaces in the
      // device console rather than as silent garbage on the server.
      console.log('[deleteAccount] server reports',
        result.deletedStorageObjects, '/', result.discoveredStorageObjects,
        'storage objects removed');

      // --- 3. Post-server local teardown ---
      try {
        if (window.BiometricAPI?.clearCredentials) {
          await window.BiometricAPI.clearCredentials().catch(() => {});
        }
      } catch (_) {}
      // Remove the detailed App Group payload while this account is still
      // signed in. The later sign-out intentionally publishes the harmless
      // signedOut marker so the widget can say "Sign in" rather than imply the
      // app was never opened.
      try { await window.WidgetAPI?.clear?.(); } catch (_) {}
      // Clear the per-user onboarded flag so a fresh sign-up on the
      // same device doesn't skip onboarding.
      try { clearUserOnboarded(session.user.id); } catch (_) {}
      // Wipe the remembered quick-login identity so a deleted account leaves
      // no email/name behind on the SignInScreen returning-user card.
      try { clearLastIdentity(); } catch (_) {}
      try {
        if (window.CapacitorPreferences?.remove) {
          const userScopedKeys = ['courses_cache', 'biometric_enabled', 'reminders_cache'];
          for (const key of userScopedKeys) {
            await window.CapacitorPreferences.remove({ key }).catch(() => {});
          }
        }
      } catch (_) {}
      // Sign out LAST so any onAuthStateChange listeners observe a clean
      // authState transition rather than firing mid-teardown.
      try { await sb.auth.signOut(); } catch (_) {}

      return { ok: true };
    },
  };

  // ── BIOMETRIC ────────────────────────────────────────────────────────────
  // Architecture (per Step 8 spec):
  //   - Supabase remains the source of truth for sessions.
  //   - Biometric (Face ID / Touch ID) acts purely as an access gate that
  //     unlocks a Keychain-stored Supabase refresh token.
  //   - On a successful prompt we load the token, hand it to
  //     refreshSession(), and let Supabase mint a fresh session pair.
  //   - The new refresh token (Supabase rotates on every refresh) is
  //     written back to Keychain so the stored credential never goes
  //     stale during normal use.
  //
  // Storage payload — a single Keychain entry under KEY:
  //   { userId, email, refresh_token, ts }
  // userId is checked against the restored session.user.id to defend
  // against multi-account confusion on a shared device.
  //
  // No secrets ever touch localStorage / sessionStorage / IndexedDB.
  // The "biometric enabled" boolean is derived from "does Keychain have
  // an entry under KEY?" — single source of truth, no second flag to
  // drift out of sync.
  const BIOMETRIC_KEY = 'vital_biometric_session_v1';
  // Per-user flag tracking whether we've already shown the "enable Face ID?"
  // post-login prompt. Localised to userId so a different account on the
  // same device gets its own one-time prompt; survives sign-out by design.
  function biomPromptKey(userId) { return `vital_biom_asked_${userId}`; }

  const BiometricAPI = {
    isSupported() {
      return !!(window.CapacitorBiometricAuth && window.CapacitorSecureStorage);
    },

    async checkAvailability() {
      if (!this.isSupported()) return { isAvailable: false, biometryType: 'none', reason: 'plugin_missing' };
      try {
        const info = await window.CapacitorBiometricAuth.checkBiometry();
        return info;
      } catch (e) {
        console.warn('[Vital biometric] checkBiometry threw', e);
        return { isAvailable: false, biometryType: 'none', reason: 'check_failed' };
      }
    },

    // Returns true if a Keychain credential exists. Does NOT prompt
    // biometric — used by the launch flow to decide whether to attempt
    // a restore at all.
    async hasStoredCredentials() {
      if (!this.isSupported()) return false;
      try {
        const value = await window.CapacitorSecureStorage.get(BIOMETRIC_KEY);
        return value !== null && value !== undefined;
      } catch (e) {
        return false;
      }
    },

    async storeCredentials({ userId, email, refresh_token }) {
      if (!this.isSupported()) return { error: { message: 'Biometric not supported' } };
      if (!userId || !refresh_token) {
        console.log('[Vital biometric] storeCredentials missing fields — userId:', !!userId, 'refresh_token:', !!refresh_token);
        return { error: { message: 'Missing userId or refresh_token' } };
      }
      try {
        await window.CapacitorSecureStorage.set(BIOMETRIC_KEY, {
          userId,
          email: email || null,
          refresh_token,
          ts: Date.now(),
        });
        console.log('[Vital biometric] storeCredentials ok');
        // Notify any subscribers (SecuritySection, etc.) that the
        // enrolment state changed so toggles refresh immediately.
        try {
          window.dispatchEvent(new CustomEvent('vital:biometric-changed', { detail: { enrolled: true } }));
        } catch (_) {}
        return { ok: true };
      } catch (e) {
        console.warn('[Vital biometric] storeCredentials failed', e);
        return { error: { message: e?.message || 'Keychain write failed' } };
      }
    },

    async loadCredentials() {
      if (!this.isSupported()) return null;
      try {
        const value = await window.CapacitorSecureStorage.get(BIOMETRIC_KEY);
        if (!value) return null;
        // SecureStorage returns objects directly when set with an object,
        // but defend against string-encoded payloads from older builds.
        if (typeof value === 'string') {
          try { return JSON.parse(value); } catch (_) { return null; }
        }
        return value;
      } catch (e) {
        return null;
      }
    },

    async clearCredentials() {
      if (!this.isSupported()) return { ok: true };
      try {
        await window.CapacitorSecureStorage.remove(BIOMETRIC_KEY);
      } catch (e) {
        // remove() throws if the key doesn't exist — treat as success.
      }
      try {
        window.dispatchEvent(new CustomEvent('vital:biometric-changed', { detail: { enrolled: false } }));
      } catch (_) {}
      return { ok: true };
    },

    // Returns true if we've already shown the post-login enrol prompt
    // for this user. We persist this per-userId in localStorage so
    // declining doesn't re-prompt on every launch.
    wasPromptShownFor(userId) {
      if (!userId) return false;
      try { return localStorage.getItem(biomPromptKey(userId)) === '1'; }
      catch (_) { return false; }
    },
    markPromptShownFor(userId) {
      if (!userId) return;
      try { localStorage.setItem(biomPromptKey(userId), '1'); } catch (_) {}
    },

    // Prompts the user. Resolves on success, returns { error } on failure
    // or cancel. Failure is intentionally a soft error so the caller can
    // fall back to the normal sign-in screen.
    async promptVerify(reason) {
      if (!this.isSupported()) return { error: { message: 'Biometric not supported' } };
      console.log('[Vital biometric] prompt shown:', reason);
      try {
        await window.CapacitorBiometricAuth.authenticate({
          reason: reason || 'Sign in with Face ID',
          cancelTitle: 'Cancel',
          allowDeviceCredential: false,
        });
        console.log('[Vital biometric] verify success');
        return { ok: true };
      } catch (e) {
        console.warn('[Vital biometric] verify failed', e?.message || e);
        return { error: { message: e?.message || 'Biometric verification failed' } };
      }
    },

    // High-level: enroll the current Supabase session for biometric unlock.
    // Caller is expected to have a fresh, valid session before calling.
    async enable(session) {
      console.log('[Vital biometric] enable() called; has session=', !!session, 'has refresh_token=', !!session?.refresh_token);
      if (!session?.user?.id) {
        return { error: { message: 'No session to enrol' } };
      }
      // The session object passed in might not have refresh_token
      // populated (e.g. after detectSessionInUrl=false on native and
      // an internal copy in React state). Fall back to a fresh
      // getSession() read so we always store the current token.
      let refresh_token = session.refresh_token;
      if (!refresh_token) {
        console.log('[Vital biometric] no refresh_token on passed session; reading from sb.auth');
        const { data } = await sb.auth.getSession();
        refresh_token = data?.session?.refresh_token;
      }
      if (!refresh_token) {
        return { error: { message: 'No refresh_token available' } };
      }
      const avail = await this.checkAvailability();
      if (!avail.isAvailable) {
        return { error: { message: avail.reason || 'Biometric not available on this device' } };
      }
      const promptRes = await this.promptVerify('Confirm Face ID to enable biometric sign-in');
      if (promptRes.error) return promptRes;
      const storeRes = await this.storeCredentials({
        userId: session.user.id,
        email: session.user.email,
        refresh_token,
      });
      console.log('[Vital biometric] enable() store result:', storeRes?.ok ? 'ok' : storeRes?.error?.message);
      return storeRes;
    },

    // High-level: prompt + restore. Used by app.jsx on launch.
    // Returns { session } on success, { error } on failure.
    // Caller is responsible for setting React state from the session.
    async tryRestore() {
      if (!this.isSupported()) return { error: { code: 'unsupported' } };
      const has = await this.hasStoredCredentials();
      if (!has) {
        console.log('[Vital biometric] no stored credentials');
        return { error: { code: 'no_credentials' } };
      }
      const promptRes = await this.promptVerify('Sign in to Vital with Face ID');
      if (promptRes.error) return { error: { code: 'verify_failed', message: promptRes.error.message } };

      const creds = await this.loadCredentials();
      if (!creds?.refresh_token) {
        console.warn('[Vital biometric] Keychain returned no refresh_token after verify');
        await this.clearCredentials();
        return { error: { code: 'no_token' } };
      }
      console.log('[Vital biometric] token restored from Keychain for userId=', creds.userId);

      const { data, error } = await sb.auth.refreshSession({ refresh_token: creds.refresh_token });
      if (error || !data?.session) {
        // Stale or revoked token — wipe Keychain so we don't loop next
        // launch, and let the caller route to the normal sign-in screen
        // with the "Please sign in again" toast (handled at the UI layer).
        console.warn('[Vital biometric] refreshSession failed; wiping Keychain', error?.message);
        await this.clearCredentials();
        return { error: { code: 'refresh_failed', message: error?.message } };
      }

      // Multi-account defence — should never trip in practice, but if
      // Supabase ever issues a session for a different user on the same
      // refresh token (token misuse / bug), we hard-fail.
      if (data.session.user.id !== creds.userId) {
        console.warn('[Vital biometric] user id mismatch — wiping');
        await this.clearCredentials();
        await window.WidgetAPI?.clear?.();
        try { await sb.auth.signOut(); } catch (_) {}
        return { error: { code: 'user_mismatch' } };
      }

      // Rotate: Supabase rotates refresh tokens on every refresh. Write
      // the new one back so the next biometric launch uses it.
      await this.storeCredentials({
        userId: data.session.user.id,
        email: data.session.user.email,
        refresh_token: data.session.refresh_token,
      });
      console.log('[Vital biometric] supabase session restored for', data.session.user.email);
      return { session: data.session };
    },
  };

  window.BiometricAPI = BiometricAPI;

  // ── PUSH NOTIFICATIONS (Step 10) ─────────────────────────────────────────
  // Two-layer design:
  //   - PushAPI handles APNs permission + token registration + token sync
  //     into public.device_tokens. The Edge Function `send-push` reads
  //     this table and posts to APNs.
  //   - LocalAPI is a small wrapper over @capacitor/local-notifications used
  //     by the in-app "Send test reminder" button so the user gets instant
  //     feedback when the wire-up is correct without waiting for an Edge
  //     Function round trip. (We ALSO call the Edge Function so both
  //     paths are exercised — see ProfileScreen.)
  //
  // apnsEnv:
  //   Dev (Xcode debug) → 'sandbox' APNs endpoint
  //   TestFlight / App Store → 'production' APNs endpoint
  //   The device token itself is identical; only the receiving server
  //   differs. We default to 'sandbox' and flip via APNS_ENV when the
  //   Edge Function is reconfigured for App Store submission.
  const PushAPI = {
    // Capacitor surfaces a plugin PROXY on web too, so `window.CapacitorPush`
    // is truthy even where the native plugin isn't implemented — calling its
    // methods then throws "PushNotifications plugin is not implemented on web".
    // Gate on the platform so the whole push UI (the Profile → Reminders
    // toggle) and every native call below is correctly off on web. Matches the
    // isNativePlatform() idiom used in supabase-config.js / auth.jsx / app.jsx.
    isSupported() { return !!(globalThis.Capacitor?.isNativePlatform?.() && window.CapacitorPush); },

    async checkPermissions() {
      if (!this.isSupported()) return { receive: 'unsupported' };
      try {
        return await window.CapacitorPush.checkPermissions();
      } catch (e) {
        console.warn('[Vital push] checkPermissions failed', e?.message || e);
        return { receive: 'unknown' };
      }
    },

    // requestAndRegister:
    //   1. Awaits listener installation FIRST so register()'s emitted
    //      events can't beat the JS subscribers to the bridge.
    //      addListener() is async in Capacitor 8 and the first event
    //      can fire while the JS handler is still attaching otherwise.
    //   2. Asks iOS for notification permission (user sees system prompt
    //      ONE time per app install; subsequent calls return the prior
    //      decision instantly).
    //   3. On 'granted', calls PushNotifications.register() — that
    //      triggers the 'registration' listener with the APNs token.
    //   4. The listener upserts the token into public.device_tokens
    //      linked to the current Supabase user.
    //   5. If no registration event arrives within 10 s, we surface a
    //      timeout so the UI can stop polling and show a useful error
    //      (most likely: missing Push capability in Xcode, or the App
    //      ID doesn't have Push enabled in the Apple Developer Portal).
    //
    // Returns:
    //   { granted: true }                  — register() fired; listener will upsert
    //   { granted: false, reason: 'denied' | 'unsupported' | 'timeout' | <err>.message }
    async requestAndRegister() {
      if (!this.isSupported()) return { granted: false, reason: 'unsupported' };
      try {
        console.log('[Vital push] ensuring listeners are installed');
        await this.installListeners();
        console.log('[Vital push] requesting permission');
        const result = await window.CapacitorPush.requestPermissions();
        if (result?.receive !== 'granted') {
          console.log('[Vital push] permission not granted:', result?.receive);
          return { granted: false, reason: result?.receive || 'denied' };
        }
        console.log('[Vital push] permission granted; calling register()');
        // Set up a one-shot Promise that resolves on the next 'registration'
        // or 'registrationError' event. Lets the caller await the actual
        // APNs response rather than just register()'s ack.
        const eventPromise = new Promise((resolve) => {
          this._oneShot = resolve;
          setTimeout(() => {
            if (this._oneShot === resolve) {
              this._oneShot = null;
              resolve({ kind: 'timeout' });
            }
          }, 10000);
        });
        await window.CapacitorPush.register();
        console.log('[Vital push] register() returned; waiting for event…');
        const ev = await eventPromise;
        if (ev.kind === 'timeout') {
          console.log('[Vital push] no registration event within 10s — likely missing aps-environment entitlement or Push capability not enabled in Xcode');
          return { granted: false, reason: 'timeout' };
        }
        if (ev.kind === 'error') {
          console.log('[Vital push] registrationError:', ev.error);
          return { granted: false, reason: ev.error };
        }
        console.log('[Vital push] registration event delivered token len=', ev.token?.length || 0);
        return { granted: true };
      } catch (e) {
        console.log('[Vital push] requestAndRegister threw', e?.message || e);
        return { granted: false, reason: e?.message || 'unknown' };
      }
    },

    // Stores an APNs token for the current user. Called by the
    // 'registration' listener (see installListeners). Safe to call
    // repeatedly — the (user_id, token) PK + upsert make it idempotent.
    //
    // Token freshness metadata (app_version, ios_version, apns_env)
    // makes it trivial to spot "stale row pointing at a different build"
    // from a SQL inspection — by far the most common cause of "token
    // exists but pushes silently fail" support tickets.
    async upsertToken(token, apnsEnv) {
      const { data: { session } } = await sb.auth.getSession();
      if (!session?.user?.id) {
        console.warn('[Vital push] upsertToken called with no session — token discarded');
        return { error: { message: 'no session' } };
      }
      // Parse iOS version from the UA, e.g.
      //   "Mozilla/5.0 (iPhone; CPU iPhone OS 17_4 like Mac OS X) …"
      // Best-effort; non-critical.
      let iosVersion = null;
      try {
        const m = (navigator.userAgent || '').match(/CPU (?:iPhone )?OS (\d+)[_.](\d+)/);
        if (m) iosVersion = `${m[1]}.${m[2]}`;
      } catch (_) {}
      const row = {
        user_id: session.user.id,
        token,
        platform: 'ios',
        apns_env: apnsEnv || (typeof __VITAL_APNS_ENV__ !== 'undefined' ? __VITAL_APNS_ENV__ : 'sandbox'),
        app_version: (typeof __VITAL_VERSION__ !== 'undefined' ? __VITAL_VERSION__ : null),
        ios_version: iosVersion,
        updated_at: new Date().toISOString(),
      };
      const { error } = await sb.from('device_tokens').upsert(row, { onConflict: 'user_id,token' });
      if (error) {
        console.warn('[Vital push] upsertToken failed', error.message);
        return { error };
      }
      console.log('[Vital push] device_tokens row upserted, env=', row.apns_env, 'app=', row.app_version, 'iOS=', row.ios_version);
      return { ok: true };
    },

    async clearTokensForCurrentUser() {
      const { data: { session } } = await sb.auth.getSession();
      if (!session?.user?.id) return { ok: true };
      const { error } = await sb.from('device_tokens').delete().eq('user_id', session.user.id);
      if (error) {
        console.warn('[Vital push] clearTokens failed', error.message);
        return { error };
      }
      return { ok: true };
    },

    // A notification tap captured before app.jsx's listener was ready (cold
    // launch from a tapped push). Returns the stashed payload once and clears
    // it, so the routing runs exactly once. Null when there's nothing pending.
    _pendingTap: null,
    consumePendingTap() { const t = this._pendingTap; this._pendingTap = null; return t; },

    // Silent per-account token provisioning, called on session-ready (login /
    // account switch). When the OS notification permission is ALREADY granted
    // (a prior explicit grant on this device), this guarantees the CURRENT
    // user has a device_tokens row so pushes are deliverable and the in-app
    // master reflects reality. It calls register() — NOT requestPermissions —
    // so it never shows a system prompt (consistent with "no cold-start ask").
    //
    // Guards (any → no-op):
    //   - push unsupported / not signed in
    //   - OS permission not 'granted' (prompt/denied → user must act first)
    //   - the account explicitly turned push OFF in-app (wasPushOptedOut)
    //   - a token already exists for this user (idempotent skip)
    async ensureRegistered() {
      if (!this.isSupported()) return { ok: false, reason: 'unsupported' };
      const { data: { session } } = await sb.auth.getSession();
      const uid = session?.user?.id;
      if (!uid) return { ok: false, reason: 'no-session' };
      if (wasPushOptedOut(uid)) return { ok: false, reason: 'opted-out' };
      const perm = await this.checkPermissions();
      if (perm?.receive !== 'granted') return { ok: false, reason: perm?.receive || 'not-granted' };
      try {
        const { data } = await sb.from('device_tokens')
          .select('token').eq('user_id', uid).limit(1);
        if ((data?.length || 0) > 0) return { ok: true, reason: 'already-registered' };
        await this.installListeners();
        await window.CapacitorPush.register(); // 'registration' listener upserts the token
        console.log('[Vital push] ensureRegistered: silent register() for', uid);
        return { ok: true, reason: 'registered' };
      } catch (e) {
        console.warn('[Vital push] ensureRegistered failed', e?.message || e);
        return { ok: false, reason: e?.message || 'error' };
      }
    },

    // Calls the send-push Edge Function in TEST mode. The Edge Function
    // authenticates via the caller's JWT, derives userId from it, and
    // only ever sends to tokens that belong to that userId — the body's
    // userId field (if present) is ignored. This is the right scope
    // for a client-triggered test push.
    //
    // FUTURE — scheduled reminder pushes (not v1):
    // The actual reminder cron will be a different code path. It will
    // call send-push with the service-role key and an explicit userId
    // looked up from the reminders table. That path does NOT reuse this
    // function — it lives server-side and bypasses RLS entirely.
    // Conflating the two would either weaken security (letting clients
    // target other users) or break the cron (forcing it to hold a user
    // JWT). Keep them separate when that branch lands.
    async sendTest() {
      const { data: { session } } = await sb.auth.getSession();
      if (!session?.access_token) return { error: { message: 'Not signed in' } };
      try {
        const resp = await fetch(
          'https://iyrxakaiowiilphxzyuq.supabase.co/functions/v1/send-push',
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ type: 'test' }),
          },
        );
        const result = await resp.json().catch(() => ({}));
        if (!resp.ok || !result.ok) {
          console.warn('[Vital push] sendTest failed', result);
          return { error: { message: result?.error || `Send failed (HTTP ${resp.status})`, detail: result?.detail } };
        }
        console.log('[Vital push] sendTest ok; sent', result.sent, '/', result.total);
        return { ok: true, sent: result.sent, total: result.total };
      } catch (e) {
        return { error: { message: e?.message || 'Network error' } };
      }
    },

    // Registers the runtime listeners. ASYNC and idempotent — await it
    // before calling register() so the JS handlers are attached before
    // any native event can fire. addListener() returns a Promise in
    // Capacitor 8; on some builds events emitted in the same tick as the
    // not-yet-resolved addListener can race the JS handler. Awaiting
    // closes that window.
    //
    // `_oneShot` is wired into 'registration' / 'registrationError' so
    // requestAndRegister() can wait for the actual APNs answer rather
    // than just register()'s native ack. Cleared by the consumer.
    async installListeners() {
      if (!this.isSupported() || this._installed) return;
      const CapPush = window.CapacitorPush;
      this._oneShot = null;

      // Build-time apns_env: 'sandbox' for Xcode debug / TestFlight
      // Internal, 'production' for TestFlight External / App Store.
      // Set via VITAL_APNS_ENV=production at build time. Hardcoding
      // 'sandbox' here (as we did originally) creates rows that lie
      // about their token's environment in production builds.
      const buildApnsEnv = (typeof __VITAL_APNS_ENV__ !== 'undefined') ? __VITAL_APNS_ENV__ : 'sandbox';
      await CapPush.addListener('registration', async (info) => {
        console.log('[Vital push] registration token len=', info?.value?.length || 0, 'env=', buildApnsEnv);
        const token = info?.value;
        // Persist the token BEFORE resolving the one-shot. Otherwise
        // requestAndRegister() resolves and the caller's refreshTokenState()
        // reads device_tokens before this upsert commits — the token looks
        // absent, the master toggle never flips on (so the user must tap it a
        // second time), and an immediate "send test" finds no device. Awaiting
        // the upsert first guarantees the row exists once the caller continues.
        if (token) {
          await this.upsertToken(token, buildApnsEnv).catch(() => {});
        }
        if (this._oneShot) { const r = this._oneShot; this._oneShot = null; r({ kind: 'token', token }); }
      });

      await CapPush.addListener('registrationError', (err) => {
        const msg = err?.error || JSON.stringify(err);
        console.log('[Vital push] registrationError', msg);
        if (this._oneShot) { const r = this._oneShot; this._oneShot = null; r({ kind: 'error', error: msg }); }
      });

      await CapPush.addListener('pushNotificationReceived', (notification) => {
        // Fires when a push arrives while the app is in the foreground. iOS now
        // shows the SYSTEM banner here too — we set
        // plugins.PushNotifications.presentationOptions = ['badge','sound','alert']
        // in capacitor.config.json — so notifications look identical whether the
        // app is foreground, backgrounded, or the phone is locked. No in-app
        // toast (it would double up with the banner).
        console.log('[Vital push] foreground notification:', notification?.title);
      });

      await CapPush.addListener('pushNotificationActionPerformed', (action) => {
        // User tapped a notification. Bridge the payload to React (app.jsx owns
        // navigation) via a window CustomEvent — same cross-module pattern as
        // 'vital:biometric-changed'. The payload's `kind` + ids drive the deep
        // link. If the tap launched the app cold, the React listener may not be
        // attached yet, so we ALSO stash it; app.jsx drains it once ready
        // (consumePendingTap). Whichever fires first clears it (no double route).
        const data = action?.notification?.data || {};
        console.log('[Vital push] action performed:', data);
        this._pendingTap = data;
        try { window.dispatchEvent(new CustomEvent('vital:notification-tap', { detail: data })); } catch (_) {}
      });

      this._installed = true;
      console.log('[Vital push] listeners installed');
    },
  };
  window.PushAPI = PushAPI;

  // Lightweight wrapper for the on-device test fire (no APNs round-trip).
  // Used by the Profile "Send test reminder" button as a sanity check;
  // the remote APNs path is exercised separately by PushAPI.sendTest().
  const LocalAPI = {
    isSupported() { return !!window.CapacitorLocal; },

    async scheduleTest(lang) {
      if (!this.isSupported()) return { error: { message: 'Not supported' } };
      try {
        const isZh = lang === 'zh';
        await window.CapacitorLocal.schedule({
          notifications: [{
            id: Date.now() & 0x7fffffff, // 32-bit positive
            title: isZh ? 'Vital · 測試提醒' : 'Vital · Test reminder',
            body:  isZh
              ? '本機通知運作正常。'
              : 'Local notifications are working on this device.',
            schedule: { at: new Date(Date.now() + 3000) }, // 3 seconds out
          }],
        });
        return { ok: true };
      } catch (e) {
        return { error: { message: e?.message || 'schedule failed' } };
      }
    },

    // ── Session reminders (local, on-device) ──────────────────────────────
    // Tag every reminder we schedule with extra.kind so we can find and
    // cancel exactly our own pending notifications without touching anything
    // else iOS may hold (e.g. the scheduleTest one-shot above).
    SESSION_REMINDER_KIND: 'session_reminder',

    async checkPermission() {
      if (!this.isSupported()) return 'unsupported';
      try {
        const p = await window.CapacitorLocal.checkPermissions();
        return p?.display || 'unknown';
      } catch { return 'unknown'; }
    },

    // Requests the iOS notification permission. Called ONLY in response to an
    // explicit user action (the Profile "課堂提醒" toggle) per Apple HIG —
    // never from a background re-schedule. After a prior 'denied', iOS does
    // not re-prompt; the caller surfaces a Settings hint instead.
    async requestPermission() {
      if (!this.isSupported()) return { granted: false, reason: 'unsupported' };
      try {
        let p = await window.CapacitorLocal.checkPermissions();
        if (p?.display === 'granted') return { granted: true };
        if (p?.display === 'denied')  return { granted: false, reason: 'denied' };
        p = await window.CapacitorLocal.requestPermissions();
        return { granted: p?.display === 'granted', reason: p?.display || 'unknown' };
      } catch (e) {
        return { granted: false, reason: e?.message || 'unknown' };
      }
    },

    // Cancels only the pending notifications we scheduled with this kind.
    async cancelByKind(kind) {
      if (!this.isSupported()) return;
      try {
        const pending = await window.CapacitorLocal.getPending();
        const mine = (pending?.notifications || []).filter(
          (n) => n?.extra?.kind === kind
        );
        if (mine.length) {
          await window.CapacitorLocal.cancel({
            notifications: mine.map((n) => ({ id: n.id })),
          });
        }
      } catch (e) {
        console.warn('[Vital local] cancelByKind failed', e?.message || e);
      }
    },

    // Replaces the full set of class reminders with `reminders`
    // ([{ id, title, body, at: Date, extra }]). Always cancels the previous
    // set first so a shrunken/edited schedule never leaves orphans.
    //
    // Does NOT prompt for permission — if not already granted we simply clear
    // any stale set and bail. The Profile toggle owns the one-time request.
    async scheduleSessionReminders(reminders) {
      if (!this.isSupported()) return { ok: false, reason: 'unsupported' };
      const display = await this.checkPermission();
      if (display !== 'granted') {
        await this.cancelByKind(this.SESSION_REMINDER_KIND);
        return { ok: false, reason: 'permission' };
      }
      await this.cancelByKind(this.SESSION_REMINDER_KIND);
      const list = Array.isArray(reminders) ? reminders : [];
      if (!list.length) return { ok: true, scheduled: 0 };
      // iOS caps pending local notifications at 64. Inputs arrive
      // soonest-first, so slicing keeps the most imminent reminders.
      const capped = list.slice(0, 60);
      try {
        await window.CapacitorLocal.schedule({
          notifications: capped.map((r) => ({
            id: r.id,
            title: r.title,
            body: r.body,
            schedule: { at: r.at, allowWhileIdle: true },
            extra: { kind: this.SESSION_REMINDER_KIND, ...(r.extra || {}) },
          })),
        });
        return { ok: true, scheduled: capped.length };
      } catch (e) {
        return { ok: false, reason: e?.message || 'schedule failed' };
      }
    },
  };
  window.LocalAPI = LocalAPI;

  // Fresh-install detection. iOS Keychain entries — including the
  // biometric refresh-token blob — persist across app uninstalls by
  // default. localStorage does NOT. The asymmetry means that after
  // an uninstall + reinstall, hasStoredCredentials() lies: it returns
  // true because of leftover Keychain data, so the "Enable Face ID?"
  // post-login prompt thinks the user is already enrolled and skips
  // itself. We resolve that by setting a localStorage install marker
  // on first launch and wiping the stale Keychain entry whenever the
  // marker is missing.
  //
  // Safe to run every launch — the marker check makes it idempotent.
  // No-op on web.
  const VITAL_INSTALL_KEY = 'vital_install_id';
  async function detectFreshInstall() {
    try {
      if (typeof localStorage === 'undefined') return;
      if (localStorage.getItem(VITAL_INSTALL_KEY)) return;
      console.log('[Vital] fresh install detected — wiping stale biometric Keychain');
      if (BiometricAPI.isSupported() && BiometricAPI.clearCredentials) {
        await BiometricAPI.clearCredentials().catch(() => {});
      }
      localStorage.setItem(VITAL_INSTALL_KEY, String(Date.now()));
    } catch (e) {
      console.warn('[Vital] fresh-install detection failed', e?.message || e);
    }
  }
  window.detectFreshInstall = detectFreshInstall;

  // ── PROFILES ──────────────────────────────────────────────────────────────
  async function getProfile(userId) {
    const { data, error } = await sb.from('profiles').select('*').eq('id', userId).maybeSingle();
    if (error) console.warn('getProfile', error);
    return data;
  }
  async function upsertProfile(userId, patch) {
    const row = { id: userId, ...patch, updated_at: new Date().toISOString() };
    const { data, error } = await sb.from('profiles').upsert(row).select().single();
    if (error) console.warn('upsertProfile', error);
    return { data, error };
  }
  async function setPrefs(userId, prefs) {
    // merge into existing prefs
    const p = await getProfile(userId);
    const merged = { ...(p?.prefs || {}), ...prefs };
    return upsertProfile(userId, { prefs: merged });
  }

  // ── NOTIFICATION PREFERENCES ──────────────────────────────────────────────
  //
  // Notification preferences — v1 scope.
  //
  // The matrix `categories[<id>].channels.{email,push}` is the storage shape.
  // The Notification Settings UI in v1 ONLY writes the `push` cell — it is the
  // single user-facing toggle per category. The `email` cells are stored at
  // the defaults but are NOT yet operational: this app has no category-driven
  // email sender. They are reserved so the schema does not need to change
  // when email delivery ships.
  //
  // Future decision point when email delivery lands: either (a) expose
  // per-channel UI inside each category (8-toggle matrix), or (b) widen the
  // existing category toggle to write BOTH cells. That is a deliberate
  // product decision — do not silently change the semantics here.
  //
  // account_security.channels is forcibly re-stamped to {email:true, push:true}
  // on every write — security alerts must remain non-disableable.
  const NOTIF_CATEGORY_IDS = [
    'renewal_reminders',
    'missing_records',
    // Covers BOTH sweeps that watch a saved course — registration deadline and
    // seat state. Deliberately ONE category: a nurse thinks "alerts about the
    // courses I saved", not "deadline alerts" vs "seat alerts", and every extra
    // toggle makes the settings list likelier to be switched off wholesale.
    'saved_course_deadline',
    // Qualification expiry (AHA BLS/ACLS/PALS cards, specialty certificates) at
    // 90/30/7 days. Defaults ON: letting a resuscitation card lapse unnoticed is
    // the failure this feature exists to prevent.
    'cert_expiry',
    // Regulatory changes from the Nursing Council (broadcast-notice). Defaults
    // ON and stays FREE for everyone — gating safety-relevant regulatory
    // information behind a paywall is not something this product does.
    'regulatory_updates',
    // Crowdsource Phase 2: pushes for approve/reject/apply outcomes on the
    // user's own submissions and reports. Best-effort — admin Edge Functions
    // fire-and-forget so a push failure never blocks the admin action.
    'community_contributions',
    // Peer nudges ("提一提") from ward-group colleagues. Push defaults ON
    // (being in a group is already opt-in); muting stops the push but the
    // in-app notification is still recorded (不打擾 ≠ 不留痕).
    'peer_nudge',
    'account_security',
  ];

  // Delivery mode for the SCHEDULED reminders (notify-cron): 'instant' = a push
  // per item, 'digest' = one daily summary push, 'off' = no push. The
  // notification center still records every item in ALL three modes
  // (不打擾 ≠ 不留痕). Community/admin event pushes are always instant.
  const NOTIF_DELIVERY_MODES = ['instant', 'digest', 'off'];

  function defaultNotificationPrefs() {
    const categories = {};
    for (const id of NOTIF_CATEGORY_IDS) {
      categories[id] = { channels: { email: true, push: true } };
    }
    return { categories, delivery: 'instant' };
  }

  function getNotificationPrefs(profile) {
    const defaults = defaultNotificationPrefs();
    const stored = profile?.prefs?.notifications;
    if (!stored || !stored.categories) return defaults;
    const merged = {
      categories: {},
      delivery: NOTIF_DELIVERY_MODES.includes(stored.delivery) ? stored.delivery : 'instant',
    };
    for (const id of NOTIF_CATEGORY_IDS) {
      const def = defaults.categories[id];
      const cur = stored.categories[id];
      merged.categories[id] = {
        channels: {
          email: cur?.channels?.email ?? def.channels.email,
          push:  cur?.channels?.push  ?? def.channels.push,
        },
      };
    }
    // account_security is always on, regardless of stored value
    merged.categories.account_security.channels = { email: true, push: true };
    return merged;
  }

  async function setNotificationPrefs(userId, patch) {
    if (!userId) return { error: { message: 'no user' } };
    const profile = await getProfile(userId);
    const current = getNotificationPrefs(profile);
    const patchCats = patch?.categories || {};
    const nextCats = {};
    for (const id of NOTIF_CATEGORY_IDS) {
      const cur = current.categories[id].channels;
      const p = patchCats[id]?.channels || {};
      nextCats[id] = {
        channels: {
          email: typeof p.email === 'boolean' ? p.email : cur.email,
          push:  typeof p.push  === 'boolean' ? p.push  : cur.push,
        },
      };
    }
    // Defense-in-depth: account_security is non-disableable regardless of
    // what the caller put in `patch`. Re-stamp every write.
    nextCats.account_security = { channels: { email: true, push: true } };
    const nextDelivery = NOTIF_DELIVERY_MODES.includes(patch?.delivery)
      ? patch.delivery : current.delivery;
    const mergedPrefs = {
      ...(profile?.prefs || {}),
      notifications: {
        // Preserve sibling notification keys (e.g. sessionReminders) — this
        // write owns `categories` + `delivery`.
        ...(profile?.prefs?.notifications || {}),
        categories: nextCats,
        delivery: nextDelivery,
      },
    };
    return upsertProfile(userId, { prefs: mergedPrefs });
  }

  // ── SESSION REMINDERS (local notifications) ───────────────────────────────
  //
  // Distinct from the server-push category matrix above. These reminders are
  // scheduled ON-DEVICE from the user's known session dates (see LocalAPI
  // .scheduleSessionReminders), so they fire offline with no APNs round-trip
  // and need no Edge Function. We only persist the user's preference here:
  //
  //   enabled  — master on/off for "remind me before each class"
  //   leadDays — fire the reminder this many days before each class (0 = day-of)
  //   hour     — local hour-of-day the reminder fires (0–23)
  //
  // Stored under prefs.notifications.sessionReminders so it travels with the
  // profile (cross-device) alongside the category matrix.
  // mode: 'full' = remind before every session, 'concise' = only the first
  // session + any session happening tomorrow (quieter for long weekly courses).
  //
  // ‼️ leadDays IS AN ARRAY (Pro can set several: 7 / 3 / 1 days out). The legacy
  // scalar shape is still read — every existing profile stores `leadDays: 1` —
  // and normalised up, so no stored value needs rewriting. notify-cron's
  // sessionReminders() accepts both for the same reason; keep the two in step.
  const SESSION_REMINDER_DEFAULTS = { enabled: true, leadDays: [1], hour: 9, mode: 'full' };
  const SESSION_REMINDER_MAX_LEAD = 14;
  // Capped because each offset is another push per class. Five enrolled courses
  // at four offsets is already 20 notifications; past that the only control she
  // has left is switching the category off, which also silences her renewals.
  // The server enforces the same cap — a client-side limit alone is decoration.
  const SESSION_REMINDER_MAX_OFFSETS = 4;
  const SESSION_REMINDER_MODES = ['full', 'concise'];

  // Accepts a number, an array, or nothing; returns a sorted, de-duplicated,
  // clamped, capped array. Never returns empty — an empty list would silently
  // disable class reminders while the toggle still read "on".
  function normaliseLeadDays(value, fallback) {
    const raw = Array.isArray(value) ? value : [value];
    const clean = [...new Set(
      raw.filter(n => Number.isInteger(n))
         .map(n => Math.max(0, Math.min(SESSION_REMINDER_MAX_LEAD, n))),
    )].sort((a, b) => b - a).slice(0, SESSION_REMINDER_MAX_OFFSETS);
    return clean.length ? clean : (fallback || [1]);
  }

  function getSessionReminderPrefs(profile) {
    const d = SESSION_REMINDER_DEFAULTS;
    const stored = profile?.prefs?.notifications?.sessionReminders;
    if (!stored) return { ...d };
    const clampInt = (v, lo, hi, fallback) =>
      Number.isInteger(v) ? Math.max(lo, Math.min(hi, v)) : fallback;
    return {
      enabled: typeof stored.enabled === 'boolean' ? stored.enabled : d.enabled,
      leadDays: normaliseLeadDays(stored.leadDays, d.leadDays),
      hour: clampInt(stored.hour, 0, 23, d.hour),
      mode: SESSION_REMINDER_MODES.includes(stored.mode) ? stored.mode : d.mode,
    };
  }

  async function setSessionReminderPrefs(userId, patch) {
    if (!userId) return { error: { message: 'no user' } };
    const profile = await getProfile(userId);
    const current = getSessionReminderPrefs(profile);
    const next = {
      enabled: typeof patch?.enabled === 'boolean' ? patch.enabled : current.enabled,
      leadDays: (patch && patch.leadDays !== undefined)
        ? normaliseLeadDays(patch.leadDays, current.leadDays)
        : current.leadDays,
      hour: Number.isInteger(patch?.hour)
        ? Math.max(0, Math.min(23, patch.hour))
        : current.hour,
      mode: SESSION_REMINDER_MODES.includes(patch?.mode) ? patch.mode : current.mode,
    };
    const mergedPrefs = {
      ...(profile?.prefs || {}),
      notifications: {
        ...(profile?.prefs?.notifications || {}),
        sessionReminders: next,
      },
    };
    const res = await upsertProfile(userId, { prefs: mergedPrefs });
    return { ...res, prefs: next };
  }

  // ── NOTIFICATION CENTER (durable history) ─────────────────────────────────
  // Reads public.user_notifications (written service-side by notify-cron + the
  // admin functions). RLS scopes every query to the owner. payload carries the
  // routing data so a tapped row deep-links exactly like a push.
  async function getNotifications(userId, { limit = 50 } = {}) {
    if (!userId) return [];
    const { data, error } = await sb
      .from('user_notifications')
      .select('id, kind, title, body, payload, priority, read_at, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) { console.warn('getNotifications', error.message); return []; }
    return data || [];
  }

  async function getUnreadNotificationCount(userId) {
    if (!userId) return 0;
    const { count, error } = await sb
      .from('user_notifications')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .is('read_at', null);
    if (error) { console.warn('getUnreadNotificationCount', error.message); return 0; }
    return count || 0;
  }

  // Mark unread rows read. ids omitted → mark ALL unread (the center's "Mark
  // all read" button); pass [id] to grey a single row when the user taps it.
  async function markNotificationsRead(userId, ids) {
    if (!userId) return { ok: true };
    let q = sb.from('user_notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('user_id', userId)
      .is('read_at', null);
    if (Array.isArray(ids) && ids.length) q = q.in('id', ids);
    const { error } = await q;
    if (error) { console.warn('markNotificationsRead', error.message); return { error }; }
    return { ok: true };
  }

  // Remove one notification (owner-only via RLS). Backs the Notification Center
  // swipe-to-delete. NOTE: needs the "own notifications delete" RLS policy — see
  // supabase-migration-user-notifications.sql; without it the delete silently
  // affects 0 rows (no error) and the row reappears on refresh.
  async function deleteNotification(id) {
    if (!id) return { ok: true, deleted: 0 };
    // .select() so we learn how many rows were ACTUALLY removed: if the delete
    // RLS policy is missing, Supabase returns no error but deletes 0 rows — the
    // caller rolls back on deleted===0 rather than letting the row silently
    // reappear on the next fetch.
    const { data, error } = await sb.from('user_notifications').delete().eq('id', id).select('id');
    if (error) { console.warn('deleteNotification', error.message); return { error }; }
    return { ok: true, deleted: (data || []).length };
  }

  // ── SAVED ─────────────────────────────────────────────────────────────────
  async function getSaved(userId) {
    const { data, error } = await sb.from('saved_courses').select('course_id').eq('user_id', userId);
    if (error) { console.warn('getSaved', error); return []; }
    return data.map(r => r.course_id);
  }
  async function toggleSaved(userId, courseId, currentlySaved) {
    if (currentlySaved) {
      return sb.from('saved_courses').delete().eq('user_id', userId).eq('course_id', courseId);
    }
    return sb.from('saved_courses').insert({ user_id: userId, course_id: courseId });
  }

  // ── ENROLLMENTS ───────────────────────────────────────────────────────────
  async function getEnrolled(userId) {
    const { data, error } = await sb.from('enrollments').select('course_id').eq('user_id', userId);
    if (error) { console.warn('getEnrolled', error); return []; }
    return data.map(r => r.course_id);
  }
  async function toggleEnrolled(userId, courseId, currentlyEnrolled) {
    if (currentlyEnrolled) {
      return sb.from('enrollments').delete().eq('user_id', userId).eq('course_id', courseId);
    }
    return sb.from('enrollments').insert({ user_id: userId, course_id: courseId });
  }

  // ── RECORDS ───────────────────────────────────────────────────────────────
  async function getRecords(userId) {
    const { data, error } = await sb.from('records')
      .select('*').eq('user_id', userId).order('completed_date', { ascending: false });
    if (error) { console.warn('getRecords', error); return []; }
    return data.map(r => ({
      id: r.id,
      date: r.completed_date,
      // ‼️ `title`/`provider` are DISPLAY properties: `zh` silently falls back
      // to English when no Chinese was ever stored. That fallback is a
      // read-time convenience and MUST NOT be written back — an editor that
      // seeds its form from here and saves the result persists the English as
      // if it were a real Chinese value, and the correction then becomes
      // invisible in a Chinese UI forever (RELEASE.md §56).
      //
      // `titleSource`/`providerSource` carry the RAW columns, null and all, so
      // an editor can tell "she has a Chinese title" from "we are showing her
      // the English one". Anything that writes MUST read these, not the two
      // above. Keep both: hundreds of read sites depend on the display shape.
      title:    { en: r.title_en,    zh: r.title_zh    || r.title_en },
      provider: { en: r.provider_en, zh: r.provider_zh || r.provider_en },
      titleSource:    { en: r.title_en ?? null,    zh: r.title_zh ?? null },
      providerSource: { en: r.provider_en ?? null, zh: r.provider_zh ?? null },
      points: Number(r.points) || 0,
      category: r.category,
      certificate: !!r.certificate_url,
      certificateUrl: r.certificate_url,
      year: r.year,
      courseId: r.course_id,
      notes: r.notes || null,
      // Optional qualification expiry (BLS/ACLS/PALS cards, specialty certs).
      // `?? null` not `|| null` so the field is absent-safe on rows cached
      // before supabase-migration-cert-expiry.sql ran.
      expiresOn: r.expires_on ?? null,
    }));
  }
  async function addRecord(userId, rec) {
    const row = {
      user_id: userId,
      course_id: rec.courseId || null,
      title_en: rec.title_en || rec.title || '',
      title_zh: rec.title_zh || null,
      provider_en: rec.provider_en || rec.provider || '',
      provider_zh: rec.provider_zh || null,
      completed_date: rec.date,
      points: Number(rec.points) || 0,
      category: rec.category || 'elective',
      // Origin is intentionally nullable: a missed caller annotation must show
      // up as "unattributed" in the activation funnel, not masquerade as manual.
      source: rec.source || null,
      certificate_url: rec.certificate_url || null,
      notes: rec.notes || null,
      // Omitted (not null) when absent so an insert still succeeds against a
      // database where supabase-migration-cert-expiry.sql has not run yet.
      ...(rec.expiresOn ? { expires_on: rec.expiresOn } : {}),
    };
    const { data, error } = await sb.from('records').insert(row).select().single();
    return { data, error };
  }
  async function deleteRecord(id) { return sb.from('records').delete().eq('id', id); }
  async function updateRecord(id, patch) {
    // patch comes from the RecordDetailSheet — translate the UI shape
    // (`{title:{en,zh}, provider:{en,zh}, date, year, points, category, notes}`)
    // into the column names the records table uses.
    const row = {};
    if (patch.title)    { row.title_en    = patch.title.en    || ''; row.title_zh    = patch.title.zh    || null; }
    if (patch.provider) { row.provider_en = patch.provider.en || ''; row.provider_zh = patch.provider.zh || null; }
    if (patch.date !== undefined)     row.completed_date = patch.date;
    // NOTE: `year` is a Postgres GENERATED column derived from completed_date —
    // attempting to write to it makes Supabase reject the entire update. We
    // silently drop patch.year and rely on the DB to recompute it.
    if (patch.points !== undefined)   row.points = Number(patch.points) || 0;
    if (patch.category !== undefined) row.category = patch.category;
    if (patch.notes !== undefined)    row.notes = patch.notes;
    // Expiry: '' from a cleared date field means "remove the expiry", which is
    // a real edit — so map falsy to null rather than dropping the key.
    if (patch.expiresOn !== undefined) row.expires_on = patch.expiresOn || null;
    const { data, error } = await sb.from('records').update(row).eq('id', id).select().single();
    if (error) console.warn('updateRecord', error);
    return { data, error };
  }

  // ── CERTIFICATE UPLOAD ────────────────────────────────────────────────────
  async function uploadCertificate(userId, recordId, file) {
    const ext = (file.name.split('.').pop() || 'pdf').toLowerCase();
    const path = `${userId}/${recordId}.${ext}`;
    const up = await sb.storage.from('certificates').upload(path, file, { upsert: true });
    if (up.error) return { error: up.error };
    const { data } = await sb.from('records').update({ certificate_url: path }).eq('id', recordId).select().single();
    return { data, path };
  }
  async function getCertificateUrl(path) {
    if (!path) return null;
    const { data } = await sb.storage.from('certificates').createSignedUrl(path, 60 * 10);
    return data?.signedUrl || null;
  }

  // ── QUALIFICATION WALLET ────────────────────────────────────────────────
  // Qualifications are independent from CNE records; a version may point back
  // to the record that earned it so both surfaces can reuse one certificate.
  function mapQualification(q) {
    const versions = (q.qualification_versions || []).map(v => ({
      id: v.id,
      qualificationId: v.qualification_id,
      sourceRecordId: v.source_record_id,
      issuer: { en: v.issuer_en || '', zh: v.issuer_zh || v.issuer_en || '' },
      issuedOn: v.issued_on,
      expiresOn: v.expires_on,
      doesNotExpire: !!v.does_not_expire,
      evidencePath: v.evidence_path,
      isCurrent: !!v.is_current,
      notes: v.notes || '',
      createdAt: v.created_at,
    })).sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
    return {
      id: q.id,
      kind: q.kind || 'other',
      title: { en: q.title_en || '', zh: q.title_zh || q.title_en || '' },
      archivedAt: q.archived_at,
      createdAt: q.created_at,
      updatedAt: q.updated_at,
      versions,
      current: versions.find(v => v.isCurrent) || versions[0] || null,
      currentVersion: versions.find(v => v.isCurrent) || versions[0] || null,
    };
  }
  async function getQualifications(userId) {
    if (!userId) return [];
    const { data, error } = await sb.from('qualifications')
      .select('*, qualification_versions(*)')
      .eq('user_id', userId).order('updated_at', { ascending: false });
    if (error) {
      // New client against a pre-migration database: keep every other feature
      // usable and render an empty wallet until the deployment gate lands.
      console.warn('getQualifications', error.message);
      return [];
    }
    return (data || []).map(mapQualification);
  }
  function qualificationRpcArgs(q) {
    return {
      p_qualification_id: q.qualificationId || null,
      p_kind: q.kind || 'other',
      p_title_en: q.titleEn || q.title_en || q.title || '',
      p_title_zh: q.titleZh || q.title_zh || null,
      p_issuer_en: q.issuerEn || q.issuer_en || q.issuer || null,
      p_issuer_zh: q.issuerZh || q.issuer_zh || null,
      p_issued_on: q.issuedOn || null,
      p_expires_on: q.doesNotExpire ? null : (q.expiresOn || null),
      p_does_not_expire: !!q.doesNotExpire,
      p_source_record_id: q.sourceRecordId || null,
      p_evidence_path: q.evidencePath || null,
      p_notes: q.notes || null,
      p_request_id: q.requestId || null,
    };
  }
  async function saveQualificationVersion(q) {
    const { data, error } = await sb.rpc('save_qualification_version', qualificationRpcArgs(q));
    if (error) console.warn('saveQualificationVersion', error.message);
    return { data, error };
  }
  async function updateQualificationVersion(versionId, q) {
    const { data, error } = await sb.rpc('update_qualification_version', {
      p_version_id: versionId,
      p_kind: q.kind || 'other', p_title_en: q.titleEn || q.title_en || q.title || '', p_title_zh: q.titleZh || q.title_zh || null,
      p_issuer_en: q.issuerEn || q.issuer_en || q.issuer || null, p_issuer_zh: q.issuerZh || q.issuer_zh || null,
      p_issued_on: q.issuedOn || null, p_expires_on: q.doesNotExpire ? null : (q.expiresOn || null),
      p_does_not_expire: !!q.doesNotExpire, p_notes: q.notes || null,
    });
    if (error) console.warn('updateQualificationVersion', error.message);
    return { data, error };
  }
  async function setQualificationArchived(id, archived) {
    const { error } = await sb.rpc('set_qualification_archived', { p_qualification_id: id, p_archived: !!archived });
    return { error };
  }
  async function deleteQualificationVersion(id) {
    const { error } = await sb.rpc('delete_qualification_version', { p_version_id: id });
    return { error };
  }
  async function deleteQualification(id) {
    const { error } = await sb.rpc('delete_qualification', { p_qualification_id: id });
    return { error };
  }

  // Did THIS rollover already happen? Pure, and separate from the RPC so it can
  // be tested against the shapes that matter rather than only through a network
  // call. Exported for scripts/test-rollover-reconciliation.mjs.
  //
  // ‼️ A MATCHING RENEWAL DATE IS NOT AN ANSWER. A dual-registered nurse can
  // hold two certificates that expire on the same day. If her secondary RPC
  // response was lost, the archive may contain her PRIMARY closing cycle with
  // exactly the date we are looking for — and matching on date alone would
  // report her secondary rollover as already done when it never ran. Date
  // right, regulatory context wrong: the failure this whole section exists to
  // prevent, reappearing in the retry path.
  //
  // So the entry must be the one we tried to close: same slot, same cycle start,
  // same closing renewal date. Reading `license` here does NOT restore the slot
  // as historical identity — it answers "was this the operation I attempted?".
  // What the record is ATTRIBUTED by remains the server-written
  // license_instance_id, which the client never sees and never supplies.
  function rolloverAlreadyCompleted({ profile, slot, cycle, expectedRenewalDate, nextRenewalDate }) {
    if (!profile) return false;
    const currentRenewal = slot === 'secondary'
      ? (profile.license2 && profile.license2.renewal_date) || null
      : profile.renewal_date || null;
    if (String(currentRenewal) !== String(nextRenewalDate)) return false;
    const archived = Array.isArray(profile.cycle_history) ? profile.cycle_history : [];
    return archived.some((e) => e && typeof e === 'object'
      && e.license === slot
      && String(e.cycle_start || '') === String((cycle && cycle.cycle_start) || '')
      && String(e.renewal_date || '') === String(expectedRenewalDate || ''));
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Licence rollover — the ONE operation that advances a regulatory cycle.
  // RELEASE.md §73 / B5.3b.
  //
  // ‼️ THE PROFILE MAY EDIT LICENCE FACTS; ONLY THIS MAY ADVANCE THE CYCLE.
  // Every screen that closes a cycle goes through here. Two call sites each
  // assembling their own RPC payload, error mapping and refetch is how one of
  // them ends up with a quiet fallback six months from now, while the other
  // still fails closed — and nobody notices until an audit.
  //
  // ‼️ THE CALLER NEVER NAMES AN IDENTITY. It passes a SLOT. The server resolves
  // slot -> persisted binding -> stable licence identity. If you ever find
  // yourself adding a licenseInstanceId parameter here, that is an architecture
  // regression, not a convenience: the client would be asserting which licence
  // its history belongs to, which is exactly the authority §72 removed.
  //
  // ‼️ THERE IS NO FALLBACK PATH. If the RPC fails, this returns a classified
  // status and the caller shows it. It must never fall back to writing
  // profiles.cycle_history directly — that is the original defect, and it would
  // reintroduce it wearing a retry.
  async function performLicenceRollover({
    userId, slot, expectedRenewalDate, cycle, nextCycleStart, nextRenewalDate,
  }) {
    // ‼️ SQLSTATE FIRST, PROSE ONLY TO DISAMBIGUATE. Matching on message text
    // alone makes the client contract hostage to English wording: rephrase an
    // error for clarity and a screen silently reclassifies a fail-closed refusal
    // as an unexpected failure. The codes are the contract; 23514 covers three
    // distinct refusals so it is the one case where the message narrows it.
    const classify = (error) => {
      const code = String(error?.code || '');
      const msg = String(error?.message || '');
      // VT409 is ours. 40001 stays recognised only so a database applied before
      // the dedicated code existed is still classified correctly, never because
      // serialization_failure is an acceptable long-term meaning for this.
      if (code === 'VT409' || code === '40001'
          || /not the one currently stored|not the current one/i.test(msg)) {
        return 'staleCycle';
      }
      if (code === '42501') return 'identityUnavailable';
      if (code === '23514') {
        return /awaiting review|unresolved/i.test(msg)
          ? 'identityNeedsResolution' : 'identityUnavailable';
      }
      if (/awaiting review|unresolved/i.test(msg)) return 'identityNeedsResolution';
      if (/no licence identity bound|archived|does not belong/i.test(msg)) return 'identityUnavailable';
      return 'unexpectedFailure';
    };

    const { data, error } = await sb.rpc('archive_license_cycle', {
      p_slot: slot,
      p_cycle: cycle,
      p_next_cycle_start: nextCycleStart,
      p_next_renewal_date: nextRenewalDate,
      p_expect_renewal_date: expectedRenewalDate,
    });
    if (!error) {
      const profile = await getProfile(userId).catch(() => null);
      return { status: 'completed', profile, entry: data };
    }

    const status = classify(error);
    if (status !== 'staleCycle') return { status, error, profile: null };

    // ── Lost-response reconciliation ────────────────────────────────────────
    // A conflict means the stored cycle is not the one we asked to close. That
    // is either (a) our own earlier attempt already succeeded and the response
    // was lost, or (b) something else genuinely moved the cycle. The RPC is
    // at-most-once, NOT retry-returns-the-same-success, so the difference has to
    // be established from state rather than assumed from the error.
    //
    // ‼️ MATCHING THE NEW RENEWAL DATE IS NOT ENOUGH. Any rollover to the same
    // period would satisfy it. We also require the archive to contain the cycle
    // we were trying to close. If both hold, this call was a duplicate of a
    // success; if only the date moved, we cannot claim credit and say so.
    const profile = await getProfile(userId).catch(() => null);
    if (!profile) return { status: 'staleCycle', error, profile: null };
    if (rolloverAlreadyCompleted({ profile, slot, cycle, expectedRenewalDate, nextRenewalDate })) {
      return { status: 'alreadyCompletedAfterRefetch', profile, error: null };
    }
    return { status: 'staleCycle', error, profile };
  }

  // The occupied current licences, as the identity registry has them (§72 C2).
  //
  // ‼️ THIS IS THE ONLY CLIENT SOURCE OF PRACTISING WALLET ITEMS, and it reads
  // the persisted bindings rather than deriving anything from the profile. The
  // difference is not stylistic: profileLicenses() renders a primary descriptor
  // for every account with the role defaulted to Registered Nurse — 556 of them
  // against 254 real slots, measured — so a wallet built from it would tell 302
  // nurses they hold a registration they never claimed.
  //
  // Rows that come back are, by construction, licences that exist. RLS restricts
  // this to her own, and the FK to license_instances means there is no such thing
  // as a binding without an identity.
  async function getLicenceIdentities(userId) {
    const { data, error } = await sb
      .from('license_slot_bindings')
      .select('slot, needs_resolution, license_instance_id, license_instances(id, registration_type, archived_at)')
      .eq('user_id', userId);
    if (error) { console.warn('getLicenceIdentities', error); return []; }
    return (data || []).map((row) => {
      const inst = row.license_instances || {};
      return {
        slot: row.slot,
        licenseInstanceId: row.license_instance_id,
        registrationType: inst.registration_type || null,
        archivedAt: inst.archived_at || null,
        needsResolution: !!row.needs_resolution,
        // ‼️ The renewal date is NOT here. Profile owns current licence facts
        // (§72's write owner) and the registry owns identity; joining them is the
        // caller's job, so neither side quietly becomes the other's source.
      };
    });
  }

  // ── Licence evidence (§72 C5–C7) ────────────────────────────────────────
  // One service; the card never touches Storage or the table directly. UI picks
  // a file and shows a result — everything about whether that document may be
  // attached is the server's decision.

  async function getLicenceEvidence(userId) {
    const { data, error } = await sb
      .from('licence_evidence')
      .select('id, license_instance_id, storage_path, covers_renewal_date, content_type, uploaded_at, superseded_at')
      .eq('user_id', userId)
      .order('uploaded_at', { ascending: false });
    if (error) { console.warn('getLicenceEvidence', error); return { error }; }
    return { data: (data || []).map(r => ({
      id: r.id,
      licenseInstanceId: r.license_instance_id,
      storagePath: r.storage_path,
      coversRenewalDate: r.covers_renewal_date,
      contentType: r.content_type,
      uploadedAt: r.uploaded_at,
      supersededAt: r.superseded_at,
    })) };
  }

  // ‼️ UPLOAD AND ATTACH ARE NOT ONE TRANSACTION, so the failure between them is
  // a real state rather than a hypothetical. If the blob lands and the RPC then
  // refuses — stale period, unresolved identity — the file is orphaned in
  // storage while nothing references it. We delete it best-effort and report the
  // refusal: the DATABASE is the authority on whether evidence exists, so a
  // failed cleanup must never be allowed to turn a refusal into a success.
  //
  // ‼️ expectedRenewalDate IS THE PERIOD SHE WAS LOOKING AT. It is captured when
  // the card is rendered and never refreshed from a later read. She can open the
  // card, renew in another session, and finish the upload minutes later; filing
  // that document against the new period would make covers_renewal_date a lie
  // told by the system rather than a fact about the paper.
  async function uploadLicenceEvidence({ userId, licenseInstanceId, expectedRenewalDate, file }) {
    if (!file) return { error: { message: 'No file selected' } };
    const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
    // uuid path, never a name or licence number: a path is read for years, and
    // one embedding a fact becomes a lie the first time she corrects it.
    const key = (globalThis.crypto?.randomUUID?.() || String(Date.now()));
    const path = `${userId}/practising/${licenseInstanceId}/${key}.${ext || 'jpg'}`;

    const up = await sb.storage.from('certificates').upload(path, file, { upsert: false });
    if (up.error) return { error: up.error };

    const { data, error } = await sb.rpc('attach_licence_evidence', {
      p_license_instance_id: licenseInstanceId,
      p_storage_path: path,
      p_expect_renewal_date: expectedRenewalDate,
      p_content_type: file.type || null,
    });
    if (error) {
      await sb.storage.from('certificates').remove([path]).catch(() => {});
      const msg = String(error.message || '');
      const status = /certificate period has changed/i.test(msg) ? 'stalePeriod'
        : /awaiting review/i.test(msg) ? 'identityNeedsResolution'
        : /no longer held|no such licence identity|not currently held/i.test(msg) ? 'identityUnavailable'
        : /no renewal date on file/i.test(msg) ? 'noPeriodToProve'
        : 'unexpectedFailure';
      return { error, status };
    }
    return { data: { id: data, storagePath: path }, status: 'attached' };
  }

  // Replacement is the same call. The server supersedes the previous document
  // only AFTER the new row exists, so a failed upload leaves her earlier
  // evidence exactly where it was — losing what she had is worse than briefly
  // keeping one file too many.
  const replaceLicenceEvidence = uploadLicenceEvidence;

  async function deleteLicenceEvidence({ evidenceId, storagePath }) {
    const { error } = await sb.rpc('delete_licence_evidence', { p_evidence_id: evidenceId });
    if (error) return { error };
    // Best effort, and deliberately after: the row is the authority. A blob left
    // behind is tidy-up; a row left behind would be a document she thinks she
    // deleted.
    if (storagePath) await sb.storage.from('certificates').remove([storagePath]).catch(() => {});
    return { data: true };
  }

  async function getLicenceEvidenceUrl(storagePath) {
    if (!storagePath) return null;
    // ‼️ Signed URLs expire and are never stored. The evidence row is the record;
    // a URL is a temporary way to look at it.
    const { data, error } = await sb.storage.from('certificates').createSignedUrl(storagePath, 60 * 5);
    if (error) { console.warn('getLicenceEvidenceUrl', error); return null; }
    return data?.signedUrl || null;
  }

  async function uploadQualificationEvidence(userId, qualificationId, versionId, file) {
    const ext = (file.name.split('.').pop() || 'pdf').toLowerCase();
    const path = `${userId}/qualifications/${qualificationId}/${versionId}.${ext}`;
    const up = await sb.storage.from('certificates').upload(path, file, { upsert: true });
    if (up.error) return { error: up.error };
    const { error } = await sb.rpc('set_qualification_evidence', { p_version_id: versionId, p_evidence_path: path });
    return error ? { error } : { path };
  }
  // Download certificate bytes for the optional Pro audit-pack appendix. This
  // is deliberately separate from signed-URL display reads: the print bridge
  // needs a self-contained data URL, not a URL that expires mid-render.
  async function getCertificateDataUrl(path) {
    if (!path) return null;
    try {
      const { data, error } = await sb.storage.from('certificates').download(path);
      if (error || !data) { if (error) console.warn('getCertificateDataUrl', error); return null; }
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onerror = () => resolve(null);
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
        reader.readAsDataURL(data);
      });
    } catch (e) {
      console.warn('getCertificateDataUrl', e);
      return null;
    }
  }

  // ── NOTE IMAGES ───────────────────────────────────────────────────────────
  // Up to 3 images can be attached to a record's `notes` field. We piggy-back
  // on the existing `certificates` bucket — uploads land at
  // `<userId>/<recordId>/note-<timestamp>.<ext>` and their paths are stored
  // in the notes column as an IMAGES:[...] JSON line (see screens RecordDetail).
  async function uploadNoteImage(userId, recordId, file) {
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const path = `${userId}/${recordId}/note-${Date.now()}-${Math.random().toString(36).slice(2, 6)}.${ext}`;
    const up = await sb.storage.from('certificates').upload(path, file, { upsert: false });
    if (up.error) return { error: up.error };
    return { path };
  }
  async function deleteNoteImage(path) {
    if (!path) return { error: null };
    return sb.storage.from('certificates').remove([path]);
  }
  async function getNoteImageUrl(path) {
    if (!path) return null;
    const { data } = await sb.storage.from('certificates').createSignedUrl(path, 60 * 60);
    return data?.signedUrl || null;
  }

  // ── COURSE ATTENDANCE (per-session ticks + custom schedules) ─────────────
  // Cross-device persistence for the in-progress course tracker. Previously
  // localStorage-only (keys cne_attendance_v1 / cne_session_dates_v1), so a
  // user signing in on a second device lost their ticks. Now: read once at
  // boot via getCourseAttendance, write each toggle via upsertCourseAttendance.
  // The client still mirrors into localStorage as an offline read cache.
  // Distinguish "feature is undeployed" (table missing, RLS missing) from
  // ordinary network/RLS errors. The hooks treat the first as "stay in
  // localStorage-only mode without toasting the user" — see the
  // hydrateAttendance / useAttendance error paths in record-extras.jsx.
  // Postgres 42P01 = undefined_table; 42501 = insufficient_privilege.
  function isMissingTableError(err) {
    if (!err) return false;
    return err.code === '42P01'
        || err.code === 'PGRST106'   // schema cache miss
        || /relation .* does not exist/i.test(err.message || '')
        || /could not find the table/i.test(err.message || '');
  }

  async function getCourseAttendance(userId) {
    if (!userId) return { ticks: {}, customSessions: {}, error: null, missingTable: false };
    let resp;
    try {
      resp = await sb.from('course_attendance')
        .select('course_id, ticks, custom_sessions')
        .eq('user_id', userId);
    } catch (e) {
      console.warn('getCourseAttendance threw', e);
      return { ticks: {}, customSessions: {}, error: e, missingTable: isMissingTableError(e) };
    }
    const { data, error } = resp || {};
    if (error) {
      console.warn('getCourseAttendance', error);
      return { ticks: {}, customSessions: {}, error, missingTable: isMissingTableError(error) };
    }
    const ticks = {};
    const customSessions = {};
    for (const r of data || []) {
      if (r.ticks && typeof r.ticks === 'object' && Object.keys(r.ticks).length) {
        ticks[r.course_id] = r.ticks;
      }
      if (Array.isArray(r.custom_sessions)) {
        customSessions[r.course_id] = r.custom_sessions;
      }
    }
    return { ticks, customSessions, error: null, missingTable: false };
  }

  // Upsert a single (user, course) row. Pass `ticks` and/or `customSessions`
  // in `patch` — undefined fields are left untouched server-side because we
  // only put defined keys into the row body, but Supabase's upsert will
  // overwrite the row entirely, so we read-then-write when only one field
  // is in the patch. This trades one extra round-trip for correctness.
  //
  // Returns { error, missingTable }. `missingTable` is true when the table /
  // policy hasn't been deployed yet — the caller (the hooks) treats that as
  // a graceful degradation rather than a save failure (no rollback, no toast).
  async function upsertCourseAttendance(userId, courseId, patch) {
    if (!userId || !courseId) {
      return { error: { message: 'missing user/course' }, missingTable: false };
    }
    const row = {
      user_id: userId,
      course_id: courseId,
      updated_at: new Date().toISOString(),
    };
    const hasTicks   = patch && Object.prototype.hasOwnProperty.call(patch, 'ticks');
    const hasCustom  = patch && Object.prototype.hasOwnProperty.call(patch, 'customSessions');
    try {
      if (hasTicks && hasCustom) {
        row.ticks = patch.ticks || {};
        row.custom_sessions = patch.customSessions;  // may be null to clear
      } else {
        // Partial update — fetch the existing row so we don't blank the other column.
        const existingResp = await sb.from('course_attendance')
          .select('ticks, custom_sessions')
          .eq('user_id', userId).eq('course_id', courseId)
          .maybeSingle();
        if (existingResp.error) {
          if (isMissingTableError(existingResp.error)) {
            return { error: existingResp.error, missingTable: true };
          }
          // Other fetch errors fall through — the upsert below will surface
          // the same condition with its own error, so don't bail early.
        }
        const existing = existingResp.data;
        row.ticks = hasTicks ? (patch.ticks || {}) : (existing?.ticks || {});
        row.custom_sessions = hasCustom
          ? patch.customSessions
          : (existing?.custom_sessions ?? null);
      }
      const { error } = await sb.from('course_attendance')
        .upsert(row, { onConflict: 'user_id,course_id' });
      if (error) {
        console.warn('upsertCourseAttendance', error);
        return { error, missingTable: isMissingTableError(error) };
      }
      return { error: null, missingTable: false };
    } catch (e) {
      console.warn('upsertCourseAttendance threw', e);
      return { error: e, missingTable: isMissingTableError(e) };
    }
  }

  // ── REMINDERS ─────────────────────────────────────────────────────────────
  async function getReminders(userId) {
    const { data, error } = await sb.from('reminders')
      .select('*').eq('user_id', userId).eq('done', false).order('due_date', { ascending: true });
    if (error) { console.warn('getReminders', error); return []; }
    return data.map(r => ({
      id: r.id, kind: r.kind, date: r.due_date,
      title: { en: r.title_en, zh: r.title_zh || r.title_en },
      sub:   { en: r.sub_en   || '', zh: r.sub_zh || r.sub_en || '' },
    }));
  }
  async function addReminder(userId, r) {
    return sb.from('reminders').insert({
      user_id: userId, kind: r.kind, due_date: r.date,
      title_en: r.title_en, title_zh: r.title_zh,
      sub_en: r.sub_en, sub_zh: r.sub_zh, course_id: r.courseId || null,
    });
  }
  async function dismissReminder(id) { return sb.from('reminders').update({ done: true }).eq('id', id); }

  // ── COURSES CACHE (Capacitor Preferences) ────────────────────────────────
  // Step 9 — offline-readable course catalogue.
  //
  // Scope (intentionally narrow):
  //   - ONLY the public course catalogue gets cached on-device.
  //   - Never auth/session tokens, records, certificates, reminders, or
  //     other private data. Those continue to require a live network +
  //     RLS-gated reads.
  //
  // Storage layout (Capacitor Preferences keys):
  //   courses_cache_schema  — integer; bumped if the cached shape stops
  //                           being compatible with the current app.
  //                           Mismatch → cache is treated as missing.
  //   courses_cache_v1      — JSON-serialised array of normalised courses.
  //   courses_cache_ts      — milliseconds-since-epoch of last save.
  //
  // Reads/writes are async (Preferences is wrapped over UserDefaults
  // on iOS). The "synchronous-feeling" hydration in app.jsx is achieved
  // by resolving the cache before triggering the first React render
  // that depends on COURSES being populated.
  const COURSES_CACHE_SCHEMA = 1;
  const KEY_COURSES_SCHEMA = 'courses_cache_schema';
  const KEY_COURSES_DATA   = 'courses_cache_v1';
  const KEY_COURSES_TS     = 'courses_cache_ts';

  const CoursesCache = {
    isSupported() { return !!window.CapacitorPreferences; },

    async load() {
      if (!this.isSupported()) return null;
      const P = window.CapacitorPreferences;
      try {
        const [schemaRes, dataRes, tsRes] = await Promise.all([
          P.get({ key: KEY_COURSES_SCHEMA }),
          P.get({ key: KEY_COURSES_DATA }),
          P.get({ key: KEY_COURSES_TS }),
        ]);
        const schema = schemaRes?.value ? parseInt(schemaRes.value, 10) : null;
        if (schema !== COURSES_CACHE_SCHEMA) {
          console.log('[Vital cache] schema mismatch or empty; ignoring cache. got=', schema, 'expected=', COURSES_CACHE_SCHEMA);
          return null;
        }
        if (!dataRes?.value) return null;
        const courses = JSON.parse(dataRes.value);
        if (!Array.isArray(courses) || courses.length === 0) return null;
        const ts = tsRes?.value ? parseInt(tsRes.value, 10) : 0;
        console.log('[Vital cache] hydrated', courses.length, 'courses; saved', ts ? new Date(ts).toISOString() : '(unknown)');
        return { courses, ts };
      } catch (e) {
        console.warn('[Vital cache] load failed', e?.message || e);
        return null;
      }
    },

    async save(courses) {
      if (!this.isSupported() || !Array.isArray(courses)) return;
      const P = window.CapacitorPreferences;
      try {
        await P.set({ key: KEY_COURSES_SCHEMA, value: String(COURSES_CACHE_SCHEMA) });
        await P.set({ key: KEY_COURSES_DATA,   value: JSON.stringify(courses) });
        await P.set({ key: KEY_COURSES_TS,     value: String(Date.now()) });
        console.log('[Vital cache] saved', courses.length, 'courses');
      } catch (e) {
        console.warn('[Vital cache] save failed', e?.message || e);
      }
    },
  };
  window.CoursesCache = CoursesCache;

  // ── COURSES (public catalogue, read from cne_courses) ────────────────────
  // Pulls all active courses from Supabase and normalises them into the
  // same UI-facing shape the screens already render (see
  // window.normaliseSupabaseCourse in data.js). On success the response
  // is mirrored into the offline cache via CoursesCache.save(); on
  // failure the cache is left as-is so an Airplane-Mode cold launch
  // still shows the last good payload.
  async function fetchCourses({ status = 'active', cache = true } = {}) {
    // Crowdsource Phase 1A: hide pending/flagged community submissions from
    // the default catalog. Restricted courses are NOT filtered here — they
    // are shown/hidden client-side via the `is_restricted` filter toggle.
    //
    // Reads cne_courses_v (a view) instead of cne_courses directly. Today
    // the view is a passthrough, but it's the stable read-side seam: when
    // the base schema evolves (column renames, lifecycle moved to a
    // satellite table, etc.) only the view DDL changes — clients on this
    // call site keep working without a coordinated deploy. See
    // supabase-migration-lifecycle.sql for the view definition.
    let q = sb.from('cne_courses_v').select('*')
      .in('verification_status', ['verified', 'community_supported'])
      .order('start_date', { ascending: true, nullsFirst: false });
    if (status) q = q.eq('lifecycle_status', status);
    const { data, error } = await q;
    if (error) {
      console.warn('fetchCourses', error);
      return { data: null, error };
    }
    const norm = (typeof window.normaliseSupabaseCourse === 'function')
      ? window.normaliseSupabaseCourse
      : (r) => r;
    const normalised = (data || []).map(norm);
    // Fire-and-forget cache write. We don't want a slow Preferences
    // write to block the UI swap to live data. Skipped for non-default
    // status fetches (e.g. status:'archived') so they don't clobber the
    // active offline cache that a cold launch reads.
    if (cache) CoursesCache.save(normalised).catch(() => {});
    return { data: normalised, error: null };
  }

  // ── Provider label dictionary (RELEASE.md §78) ────────────────────────────
  // ‼️ THE LABEL IS A PROPERTY OF THE IDENTITY, NOT OF A COURSE. The Discover
  // provider facet groups by `provider_entity_id`; the text on the chip comes
  // from here and NEVER from a course's provider_full_en/zh. Rendering evidence
  // under an identity key would make the chip read whichever course was
  // iterated first — one provider, two spellings, a different label per load.
  //
  // Served by `provider_labels_v`, an owner-privileged 3-column view: anon can
  // read the dictionary but still cannot read provider_entities. It is NOT a
  // join inside cne_courses_v, because that view is `security_invoker = true`
  // and the join would silently evaluate as the caller and return nothing.
  // ~105 rows, fetched once per session, cached so an offline cold start still
  // has real names.
  const PROVIDER_LABEL_CACHE_KEY = 'vital.providerLabels.v1';

  async function fetchProviderLabels() {
    const { data, error } = await sb
      .from('provider_labels_v')
      .select('provider_entity_id, label_en, label_zh');
    if (error) {
      console.warn('fetchProviderLabels', error);
      // Fall back to the last good dictionary rather than to nothing: a chip
      // reading a bare UUID is worse than a slightly stale name.
      try {
        const raw = localStorage.getItem(PROVIDER_LABEL_CACHE_KEY);
        if (raw) return { data: JSON.parse(raw), error: null, stale: true };
      } catch (_) { /* private mode */ }
      return { data: null, error };
    }
    try { localStorage.setItem(PROVIDER_LABEL_CACHE_KEY, JSON.stringify(data || [])); }
    catch (_) { /* quota / private mode — the dictionary is re-fetchable */ }
    return { data: data || [], error: null };
  }

  function cachedProviderLabels() {
    try {
      const raw = localStorage.getItem(PROVIDER_LABEL_CACHE_KEY);
      const arr = raw ? JSON.parse(raw) : null;
      return Array.isArray(arr) ? arr : null;
    } catch (_) { return null; }
  }

  async function fetchCourseById(id) {
    // Same view-vs-table rationale as fetchCourses. NOTE: intentionally NOT
    // filtering by lifecycle_status here — deep links to archived /
    // rejected_low_confidence courses should still resolve so users see
    // an honest "no longer offered" / "data unverified" message in the
    // detail UI rather than a confusing 404. enrolmentStatusInfo() and
    // isPastCourse() both gate on lifecycleStatus so the CTA is correct.
    const { data, error } = await sb.from('cne_courses_v').select('*').eq('id', id).maybeSingle();
    if (error) { console.warn('fetchCourseById', error); return null; }
    if (!data) return null;
    return (typeof window.normaliseSupabaseCourse === 'function')
      ? window.normaliseSupabaseCourse(data)
      : data;
  }

  // ── PROVIDERS (cne_providers) ─────────────────────────────────────────────
  // Returns the directory of NCHK-accredited providers, plus a live
  // course count per provider (active rows only) so the index screen
  // can show "23 courses" next to each entry.
  async function fetchProviders() {
    const [provRes, countRes] = await Promise.all([
      sb.from('cne_providers').select('*').order('code', { ascending: true }),
      // Use the view + lifecycle_status, mirroring fetchCourses's filter so
      // counts stay consistent with what users actually see in Discover.
      // Also gate on verification_status so community_supported ‘soft’
      // entries are counted but pending/flagged ones are not.
      sb.from('cne_courses_v').select('provider_code')
        .in('verification_status', ['verified', 'community_supported'])
        .eq('lifecycle_status', 'active'),
    ]);
    if (provRes.error) {
      console.warn('fetchProviders', provRes.error);
      return { data: null, error: provRes.error };
    }
    const counts = {};
    (countRes.data || []).forEach(row => {
      counts[row.provider_code] = (counts[row.provider_code] || 0) + 1;
    });
    const norm = (provRes.data || []).map(p => ({
      code: p.code,
      full_name_en: p.full_name_en || p.code,
      full_name_zh: p.full_name_zh || p.full_name_en || p.code,
      is_active: p.is_active !== false,
      source_url: p.source_url || null,
      course_count: counts[p.code] || 0,
    }));
    return { data: norm, error: null };
  }

  // ── CROWDSOURCE: SUBMISSIONS / REPORTS / EVIDENCE (Phase 1A) ──────────────
  // Lets nurses propose new courses the platform hasn't catalogued, and
  // report issues with existing courses. All writes are RLS-gated to the
  // submitter; admin/moderator approval flows live in Phase 1B Edge Functions.
  //
  // payload_schema_version + normaliseSubmissionPayload lets the jsonb
  // payload shape evolve without losing old rows. Bump SUBMISSION_SCHEMA_VERSION
  // when the shape changes, add a migration branch in normaliseSubmissionPayload.

  const SUBMISSION_SCHEMA_VERSION = 3;

  function normaliseSubmissionPayload(payload, version) {
    const v = version || 1;
    if (v === SUBMISSION_SCHEMA_VERSION) return payload;
    // v2 → v3: the summary became bilingual (description_short_zh), because an
    // approved submission renders in both language builds. Rows written before
    // the change carry only the English one. NOTE: every older version must
    // keep a branch here — reaching the throw below would break the admin
    // review list for rows that are perfectly valid.
    if (v === 2) {
      return { ...payload, description_short_zh: payload?.description_short_zh ?? null };
    }
    if (v === 1) {
      return {
        ...payload,
        title_zh: payload?.title_zh ?? null,
        fee_hkd: payload?.fee_hkd ?? null,
        language_en: payload?.language_en ?? null,
        teaching_mode_en: payload?.teaching_mode_en ?? null,
        time_text: payload?.time_text ?? null,
        description_short_en: payload?.description_short_en ?? null,
        description_short_zh: payload?.description_short_zh ?? null,
        url: payload?.url ?? null,
      };
    }
    throw new Error('Unknown submission schema version: ' + v);
  }

  // Upload an evidence file to the 'submissions' bucket.
  // Path: <userId>/<owningId>/<filename>. owningId is the submission or
  // report row id — required so RLS scopes uploads to the submitter and
  // the admin reader policy can find them by foldername[1] = userId.
  async function uploadSubmissionEvidence(userId, kind, owningId, file) {
    const ext = (file.name?.split('.').pop() || 'bin').toLowerCase();
    const safeName = (file.name || 'evidence').replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${userId}/${kind}/${owningId}/${Date.now()}-${safeName}`;
    const up = await sb.storage.from('submissions').upload(path, file, { upsert: false });
    if (up.error) return { error: up.error };
    return { path };
  }

  // ‼️ ONE RULE, TWO CALLERS — the UI that OFFERS the action and the code that
  // PERFORMS it. A community contribution needs something a reviewer can check
  // against: an official link, or at least one stored file. With neither there is
  // nothing to verify, so submitCourse rolls the row back.
  //
  // That rollback is CORRECT. What was wrong is that only submitCourse knew the
  // rule: SubmitCourseSheet disables its button via its own copy
  // (`hasVerifiable`), while LogSheet's co-submit checkbox offered the action
  // with no certificate and no URL — a control whose handler could never
  // succeed, ending in a created row being deleted and an error that blamed an
  // upload which never happened.
  //
  // Exporting it is what stops the offer and the outcome from disagreeing.
  function submissionIsVerifiable({ payload, evidenceCount } = {}) {
    return hasHttpUrl(payload?.url)
        || hasHttpUrl(payload?.registration_url)
        || (Number(evidenceCount) || 0) > 0;
  }

  // ‼️ WHICH CONTRIBUTIONS THE COMMUNITY CATALOGUE CAN USE AT ALL — asked BEFORE
  // offering, exactly like submissionIsVerifiable. Measured live 2026-11-01 over
  // all 116 submissions: a course that had ALREADY ENDED when it was shared was
  // later used by another nurse 3 times in 59; one still upcoming, 13 in 23. And
  // a nurse's practising certificate reached the review queue as a "course",
  // because LogSheet offered co-submit for a document the AI had already
  // classified as doc_type 'qualification'. Two reasons, one answer:
  //   course_ended — its LAST day is before today in Hong Kong
  //   not_a_course — a credential (practising certificate, degree, …)
  // ‼️ HONG KONG'S DAY, not the device's and not UTC's (the regulator's day; see
  // the "two todays" note in RELEASE.md). The database trigger
  // course_submissions_refuse_ended (supabase-migration-submission-ended-gate.sql)
  // applies the SAME last-day rule, so an old app build cannot get round it.
  // A course held TODAY is still shareable: it has not ended.
  function hkTodayIso(now) {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Hong_Kong', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(now instanceof Date ? now : new Date());
  }

  // The last calendar day a course runs: the latest of end_date, start_date and
  // every scheduled session. null when the payload carries no usable date —
  // then nothing can be judged, and nothing is refused on this ground.
  function courseLastDay(payload) {
    const days = [payload?.end_date, payload?.start_date,
      ...(Array.isArray(payload?.sessions_list) ? payload.sessions_list : [])]
      .map((v) => String(v || '').slice(0, 10))
      .filter((v) => /^\d{4}-\d{2}-\d{2}$/.test(v));
    days.sort();
    return days.length ? days[days.length - 1] : null;   // not .at(-1): older iOS WebViews lack it
  }

  function submissionIsShareable({ payload, docType, today } = {}) {
    if (docType === 'qualification') return { ok: false, reason: 'not_a_course' };
    const last = courseLastDay(payload);
    if (last && last < (today || hkTodayIso())) return { ok: false, reason: 'course_ended', lastDay: last };
    return { ok: true, reason: null };
  }

  const hasHttpUrl = (value) => {
    try {
      const u = new URL(String(value || '').trim());
      return u.protocol === 'https:' || u.protocol === 'http:';
    } catch (_) {
      return false;
    }
  };

  async function removeSubmissionEvidence(paths) {
    const keys = [...new Set((paths || []).filter(Boolean))];
    if (!keys.length) return { error: null };
    const { error } = await sb.storage.from('submissions').remove(keys);
    return { error: error || null };
  }

  async function uploadEvidenceBatch(userId, kind, owningId, files) {
    const uploadedPaths = [];
    const failedFiles = [];
    for (const file of (files || [])) {
      const { path, error } = await uploadSubmissionEvidence(userId, kind, owningId, file);
      if (error || !path) {
        failedFiles.push({
          name: file?.name || 'evidence',
          message: error?.message || 'upload failed',
        });
      } else {
        uploadedPaths.push(path);
      }
    }
    return { uploadedPaths, failedFiles };
  }

  // Persist evidence paths and read them back. Supabase can return an ambiguous
  // network error after the UPDATE has committed, so the row is the authority,
  // not the response status.
  async function persistEvidencePaths(table, id, uploadedPaths) {
    let updateError = null;
    if (uploadedPaths.length) {
      const update = await sb.from(table)
        .update({ evidence_urls: uploadedPaths })
        .eq('id', id);
      updateError = update.error || null;
    }
    const readback = await sb.from(table)
      .select('evidence_urls')
      .eq('id', id)
      .maybeSingle();
    const persistedPaths = Array.isArray(readback.data?.evidence_urls)
      ? readback.data.evidence_urls.filter(Boolean)
      : [];
    return {
      persistedPaths,
      error: readback.error || updateError,
    };
  }

  async function rollbackContribution(table, id, objectPaths) {
    const remove = await removeSubmissionEvidence(objectPaths);
    const deletion = await sb.from(table).delete().eq('id', id);
    return {
      incomplete: !!(remove.error || deletion.error),
      removeError: remove.error || null,
      deleteError: deletion.error || null,
    };
  }

  // ── ARTIFACT DIGEST, SCHEME v1 ────────────────────────────────────────────
  // ‼️ WHAT THIS PRODUCES IS A *CLAIM*, NOT PROOF. The server recomputes the
  // same value from the bytes actually in storage during promotion and refuses
  // a promotion where the two disagree. A claim may raise NEEDS_CONFIRMATION;
  // it may never produce an ALREADY_* verdict and never keys a unique index.
  //
  // ‼️ THE CONSTRUCTION MUST MATCH THE SERVER'S EXACTLY:
  //   SHA-256 each file → sort the hex digests → hash a versioned manifest of
  //   count plus length-delimited digests.
  // Sorting makes upload order and storage.list() order irrelevant; the length
  // delimiters keep {A,B} distinct from a single concatenated AB. FILENAMES ARE
  // EXCLUDED ON PURPOSE — the same bytes under a renamed photo are the same
  // artifact, which is exactly the case this guard exists to catch.
  async function computeArtifactDigest(files) {
    const list = (files || []).filter(Boolean);
    if (!list.length) return null;
    if (!globalThis.crypto?.subtle) return null;   // degrade honestly, never guess
    const hex = (buf) => Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0')).join('');
    try {
      const digests = [];
      for (const file of list) {
        digests.push(hex(await crypto.subtle.digest('SHA-256', await file.arrayBuffer())));
      }
      digests.sort();
      const manifest = `v1:${digests.length}:` + digests.map((d) => `${d.length}:${d}`).join('');
      return `v1:${hex(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(manifest)))}`;
    } catch (e) {
      console.warn('computeArtifactDigest failed (submitting without a claim)', e?.message || e);
      return null;
    }
  }

  // Ask the server what it already knows about this course, for THIS user.
  // ‼️ ADVISORY ONLY. It can be stale by the time the row is written; the unique
  // indexes are what actually converge a race. Never treat it as a lock.
  async function checkSubmissionCandidate({ payload, artifactDigestClaim, sourceRecordId } = {}) {
    const { data, error } = await sb.rpc('submission_candidate_check', {
      p_payload: payload || {},
      p_artifact_digest_claim: artifactDigestClaim || null,
      p_source_record_id: sourceRecordId || null,
    });
    if (error) {
      console.warn('checkSubmissionCandidate', error);
      return { data: null, error };
    }
    return { data, error: null };
  }

  // ‼️ THE HINT IS A PATCH, NOT A VALUE — WHICH IS WHY THERE ARE TWO ARGUMENTS.
  // On an UPDATE a lone null cannot mean both "the caller did not mention the
  // hint" and "the nurse cleared it": the first must PRESERVE, the second must
  // WRITE NULL. §88 gave the RPC an explicit `_set` flag for exactly this, and
  // collapsing them here would silently erase a correct selection on every edit
  // made from a screen that has no picker.
  //
  //   providerEntityIdHintSet omitted/false  → leave it exactly as it is
  //   set true,  hint = X                    → change it to X
  //   set true,  hint = null                 → clear it, deliberately
  async function editMySubmission({ submissionId, payload, isRestricted, restrictionNote, confirmToken,
                                    providerEntityIdHint, providerEntityIdHintSet } = {}) {
    if (!submissionId) return { data: null, error: { message: 'submissionId required' } };
    const { data, error } = await sb.rpc('edit_own_submission', {
      p_submission_id: submissionId,
      p_payload: payload || {},
      p_is_restricted: isRestricted ?? null,
      p_restriction_note: restrictionNote ?? null,
      p_confirm_token: confirmToken || null,
      p_provider_entity_id_hint: providerEntityIdHint ?? null,
      p_provider_entity_id_hint_set: !!providerEntityIdHintSet,
      // ‼️ THE SECOND WRITER, AND IT NEVER RESTAMPED. This RPC replaces `payload`
      // wholesale; leaving the version behind means a row edited by a newer
      // client keeps an older decoder. No `_set` companion is needed here — the
      // version describes the payload being sent, so "did not mention it" and
      // "wants it unchanged" are the same request.
      p_payload_schema_version: SUBMISSION_SCHEMA_VERSION,
    });
    if (error) { console.warn('editMySubmission', error); return { data: null, error }; }
    return { data, error: null };
  }

  // Is editing a PENDING submission switched on server-side yet?
  // ‼️ NOT A SECURITY BOUNDARY. edit_own_submission refuses a pending row on its
  // own while the flag is false; this exists so the UI does not offer a button
  // the server will decline. Defaults to false on any error — hiding an
  // affordance is the safe failure, promising one is not.
  async function isPendingEditEnabled() {
    try {
      const { data, error } = await sb.rpc('submission_pending_edit_enabled');
      if (error) return false;
      return data === true;
    } catch (_) { return false; }
  }

  async function withdrawMySubmission(submissionId) {
    if (!submissionId) return { data: null, error: { message: 'submissionId required' } };
    const { data, error } = await sb.rpc('withdraw_own_submission', {
      p_submission_id: submissionId,
    });
    if (error) { console.warn('withdrawMySubmission', error); return { data: null, error }; }
    return { data, error: null };
  }

  // A unique-violation from either submission-guard index, however it reaches us.
  // ‼️ PostgREST does not guarantee a structured `constraint` field, so the code
  // is the signal and the message is only corroboration — the same reason the
  // server reads GET STACKED DIAGNOSTICS instead of parsing text.
  function isSubmissionDuplicateError(error) {
    if (!error) return false;
    if (error.code === '23505') return true;
    const blob = `${error.message || ''} ${error.details || ''}`.toLowerCase();
    return blob.includes('course_submissions_one_open_per');
  }

  // Create a course submission, then upload/persist evidence.
  //
  // ‼️ THE SINGLE WRITE DOOR. Both client entry points — SubmitCourseSheet and
  // LogSheet's co-submit checkbox — call this one function, which is why routing
  // it through create_course_submission gives BOTH of them the guard. A third
  // door added later inherits it by using this function; one that inserts
  // directly does not, and the unique indexes are what catch that.
  //
  // Verdicts come back as DATA, not errors: the caller needs the existing
  // submission id to offer 查看 / 修改 / 撤回 rather than a dead end.
  async function submitCourse({ userId, payload, isRestricted, restrictionNote, evidenceFiles, sourceOrigin, sourceRecordId, confirmToken, providerEntityIdHint }) {
    if (!userId) return { error: { message: 'not signed in' } };
    // ‼️ REFUSED BEFORE ANYTHING IS CREATED OR UPLOADED. The UI already hides the
    // offer (it asks submissionIsShareable first); this is the door's own copy
    // of the answer, and the database trigger is the one an old build meets.
    const shareable = submissionIsShareable({ payload });
    if (!shareable.ok) {
      return { error: {
        code: shareable.reason === 'course_ended' ? 'submission_course_ended' : 'submission_not_a_course',
        message: shareable.reason === 'course_ended'
          ? 'This course has already ended. The community catalogue lists courses nurses can still join.'
          : 'Only courses can be shared to the community catalogue.',
      } };
    }
    const artifactDigestClaim = await computeArtifactDigest(evidenceFiles);
    const created = await sb.rpc('create_course_submission', {
      p_payload: payload || {},
      p_artifact_digest_claim: artifactDigestClaim,
      p_is_restricted: !!isRestricted,
      p_restriction_note: restrictionNote || null,
      p_source_origin: sourceOrigin || 'manual_submit',
      p_source_record_id: sourceRecordId || null,
      p_confirm_token: confirmToken || null,
      // ‼️ THE PICKER'S CHANNEL. BOTH submission doors fill it now: §92 wired
      // SubmitCourseSheet and §99 wired LogSheet's co-submit. It is still `null`
      // on any submission where the nurse did not choose an organisation, and
      // the RPC then behaves exactly as its 7-arg predecessor did — optional is
      // the contract, not a transitional state.
      //
      // ‼️ `|| null`, NOT `?? null`, AND NOT OMITTED. An omitted key would take
      // the SQL default and read the same today — but the day B1-C passes a
      // value, a typo in the caller's field name would silently fall back to
      // the default instead of failing, which is the whole class of defect this
      // channel exists to remove (§88/§89/§90 all read a column the submit door
      // could not write, and nothing said so).
      p_provider_entity_id_hint: providerEntityIdHint || null,
      // ‼️ THE CLIENT SAYS WHICH SHAPE IT BUILT, because it is the only party
      // that knows. §79 moved this write into the RPC and dropped the version on
      // the way: the door stamped the column-era literal 1 while db.js was
      // building v3, and seven rows carry the wrong decoder because of it.
      //
      // The SQL default (3) covers builds that predate this line — it is a
      // fleet-compatibility ruling, recorded in
      // scripts/payload-schema-version-policy.json. Sending it EXPLICITLY is
      // what keeps the default a safety net rather than the mechanism.
      p_payload_schema_version: SUBMISSION_SCHEMA_VERSION,
    });
    if (created.error) {
      // The database trigger (course_submissions_refuse_ended) is the same rule;
      // reaching it means this device's clock or build disagreed with it.
      if (/course_already_ended/.test(`${created.error.message || ''}`)) {
        return { error: { code: 'submission_course_ended', message: 'This course has already ended. The community catalogue lists courses nurses can still join.' } };
      }
      return { error: created.error };
    }

    const verdict = created.data?.verdict;
    // Nothing was written and nothing was uploaded — hand the caller the row it
    // already has so the UI can route to it.
    if (verdict !== 'CREATED') {
      return {
        verdict,
        existing: created.data?.own || [],
        confirmToken: created.data?.confirm_token || null,
      };
    }

    const insert = { data: { id: created.data.submission_id } };
    const submissionId = created.data.submission_id;

    const requestedFiles = evidenceFiles || [];
    const { uploadedPaths, failedFiles } =
      await uploadEvidenceBatch(userId, 'submission', submissionId, requestedFiles);
    const persisted = await persistEvidencePaths(
      'course_submissions',
      submissionId,
      uploadedPaths,
    );
    const persistedSet = new Set(persisted.persistedPaths);
    const unreferencedPaths = uploadedPaths.filter((path) => !persistedSet.has(path));
    const unreferencedCleanup = await removeSubmissionEvidence(unreferencedPaths);
    const isVerifiable = submissionIsVerifiable({
      payload, evidenceCount: persisted.persistedPaths.length,
    });

    if (!isVerifiable) {
      const rollback = await rollbackContribution(
        'course_submissions',
        submissionId,
        uploadedPaths,
      );
      return {
        // ‼️ THIS IS NOT AN UPLOAD FAILURE, AND IT USED TO SAY IT WAS. The old
        // code was `evidence_upload_failed` with "Evidence could not be saved.
        // Please try again." — reported with `failedFiles: []`, because nothing
        // had failed: there was simply nothing to verify the submission with.
        // "Please try again" was worse than useless, since an identical retry
        // fails identically. Diagnosing one real occurrence cost a round trip
        // through the production database on 2026-09-02.
        error: {
          code: 'submission_not_verifiable',
          message: 'A course submission needs a certificate or an official link.',
          failedFiles: failedFiles.map((f) => f.name),
          submissionId,
          cleanupIncomplete: rollback.incomplete || !!unreferencedCleanup.error,
        },
      };
    }

    const partial = failedFiles.length > 0
      || !!persisted.error
      || unreferencedPaths.length > 0
      || !!unreferencedCleanup.error;
    const finalized = await adminCallEdge('finalize-submission-evidence', {
      kind: 'submission', id: submissionId,
      url: payload?.url || payload?.registration_url || null,
    });
    // ‼️ THE SECOND PLACE A DUPLICATE CAN SURFACE, AND THE ONLY ONE THE INDEXES
    // CAN CATCH. Both unique indexes ignore 'draft', so two drafts of the same
    // submission coexist happily and collide only here, at promotion. The Edge
    // Function has already compensated the losing draft and its evidence
    // server-side by the time this returns — client cleanup is a second line,
    // never the guarantee, because a dropped connection would otherwise strand
    // the objects forever.
    const conflictCode = finalized.error?.code || finalized.data?.code;
    if (conflictCode === 'ALREADY_PENDING' || conflictCode === 'ALREADY_APPROVED') {
      const winnerId = finalized.error?.winnerId || finalized.data?.winnerId || null;
      await rollbackContribution('course_submissions', submissionId, uploadedPaths);
      return {
        verdict: conflictCode,
        existing: winnerId ? [{ id: winnerId, status: finalized.error?.winnerStatus
          || finalized.data?.winnerStatus || null }] : [],
      };
    }
    if (conflictCode === 'ARTIFACT_DIGEST_MISMATCH') {
      await rollbackContribution('course_submissions', submissionId, uploadedPaths);
      return {
        error: {
          code: 'artifact_digest_mismatch',
          message: 'The evidence changed while it was being uploaded. Please try again.',
          submissionId,
        },
      };
    }
    if (finalized.error) {
      if (isSubmissionDuplicateError(finalized.error)) {
        await rollbackContribution('course_submissions', submissionId, uploadedPaths);
        return { verdict: 'ALREADY_PENDING', existing: [] };
      }
      return { error: { ...finalized.error, submissionId, cleanupIncomplete: true } };
    }
    return {
      data: { ...insert.data, status: 'pending', evidence_urls: persisted.persistedPaths },
      warning: partial ? {
        code: 'evidence_upload_partial',
        failedFiles: failedFiles.map((f) => f.name),
        persistedCount: persisted.persistedPaths.length,
        requestedCount: requestedFiles.length,
      } : null,
    };
  }

  // Report an issue with an existing course. fieldPath whitelist enforcement
  // lives in the Phase 1B apply-report Edge Function — at submit time we
  // just accept what the user reports.
  async function reportCourseIssue({ userId, courseId, reportType, fieldPath, currentValue, suggestedValue, reason, evidenceFiles }) {
    if (!userId) return { error: { message: 'not signed in' } };
    if (!courseId) return { error: { message: 'courseId required' } };
    const insert = await sb.from('course_reports').insert({
      reporter_user_id: userId,
      course_id: String(courseId),
      report_type: reportType || 'other',
      field_path: fieldPath || null,
      current_value: currentValue != null ? String(currentValue) : null,
      suggested_value: suggestedValue != null ? String(suggestedValue) : null,
      reason: reason || null,
      status: 'draft',
    }).select().single();
    if (insert.error) return { error: insert.error };
    const reportId = insert.data.id;

    const requestedFiles = evidenceFiles || [];
    const { uploadedPaths, failedFiles } =
      await uploadEvidenceBatch(userId, 'report', reportId, requestedFiles);
    const persisted = await persistEvidencePaths('course_reports', reportId, uploadedPaths);
    const persistedSet = new Set(persisted.persistedPaths);
    const unreferencedPaths = uploadedPaths.filter((path) => !persistedSet.has(path));
    const unreferencedCleanup = await removeSubmissionEvidence(unreferencedPaths);
    const hasNonEvidenceBasis = !!String(reason || '').trim()
      || !!fieldPath
      || suggestedValue != null;
    const isActionable = hasNonEvidenceBasis || persisted.persistedPaths.length > 0;

    if (!isActionable) {
      const rollback = await rollbackContribution('course_reports', reportId, uploadedPaths);
      return {
        // ‼️ SAME LIE, SIBLING PATH. A report with no reason, no field, no
        // suggested value and no file is not an upload that failed — it is a
        // report with nothing in it.
        error: {
          code: 'report_not_actionable',
          message: 'A report needs a reason, a suggested correction, or a file.',
          failedFiles: failedFiles.map((f) => f.name),
          reportId,
          cleanupIncomplete: rollback.incomplete || !!unreferencedCleanup.error,
        },
      };
    }

    const partial = failedFiles.length > 0
      || !!persisted.error
      || unreferencedPaths.length > 0
      || !!unreferencedCleanup.error;
    const finalized = await adminCallEdge('finalize-submission-evidence', {
      kind: 'report', id: reportId,
    });
    if (finalized.error) {
      return { error: { ...finalized.error, reportId, cleanupIncomplete: true } };
    }
    return {
      data: { ...insert.data, status: 'pending', evidence_urls: persisted.persistedPaths },
      warning: partial ? {
        code: 'evidence_upload_partial',
        failedFiles: failedFiles.map((f) => f.name),
        persistedCount: persisted.persistedPaths.length,
        requestedCount: requestedFiles.length,
      } : null,
    };
  }

  // Read back this user's submission history (powers Profile → My contributions).
  // Wraps each row's payload through normaliseSubmissionPayload so callers
  // can assume a consistent shape even if SUBMISSION_SCHEMA_VERSION has
  // moved on.
  async function fetchMySubmissions(userId) {
    if (!userId) return { data: [], error: null };
    const { data, error } = await sb.from('course_submissions')
      .select('*')
      .eq('submitter_user_id', userId)
      .order('created_at', { ascending: false });
    if (error) return { data: null, error };
    const normalised = (data || []).map(row => ({
      ...row,
      payload: normaliseSubmissionPayload(row.payload, row.payload_schema_version),
    }));
    return { data: normalised, error: null };
  }

  // Mirror of fetchMySubmissions for the user's own course_reports.
  // RLS confines the result to rows the caller authored.
  async function fetchMyReports(userId) {
    if (!userId) return { data: [], error: null };
    const { data, error } = await sb.from('course_reports')
      .select('*')
      .eq('reporter_user_id', userId)
      .order('created_at', { ascending: false });
    if (error) return { data: null, error };
    return { data: data || [], error: null };
  }

  // The user's own app_feedback (comments/suggestions), for the 我的貢獻
  // Comments section — so they can see each comment's status + the team's
  // reply (admin_reply). RLS ("feedback own select") confines it to the caller.
  async function fetchMyFeedback(userId) {
    if (!userId) return { data: [], error: null };
    const { data, error } = await sb.from('app_feedback')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error) return { data: null, error };
    return { data: data || [], error: null };
  }

  // 我的貢獻 reward strip: lifetime AI-scan credits this user earned from
  // contributions — kind 'bonus_approval' (approved course submission) and
  // 'bonus_report' (applied field correction) in scan_credit_ledger (RLS:
  // scan_credit_read_own). Distinct from getScanCreditStatus, which is the
  // CURRENT balance (earned − spent); this is the lifetime "thank you" tally.
  // Never throws — returns null on any error so the strip just hides.
  async function fetchMyContributionRewards(userId) {
    if (!userId) return null;
    try {
      const { data, error } = await sb.from('scan_credit_ledger')
        .select('delta, kind')
        .eq('user_id', userId)
        .in('kind', ['bonus_approval', 'bonus_report']);
      if (error) { console.warn('fetchMyContributionRewards', error); return null; }
      const sumKind = (k) => (data || [])
        .filter(r => r.kind === k && r.delta > 0)
        .reduce((acc, r) => acc + r.delta, 0);
      const fromApprovals = sumKind('bonus_approval');
      const fromReports = sumKind('bonus_report');
      return { earned: fromApprovals + fromReports, fromApprovals, fromReports };
    } catch (e) {
      console.warn('fetchMyContributionRewards', e);
      return null;
    }
  }

  // ── APP FEEDBACK (Profile → Leave a comment or suggestion) ───────────────
  // General product feedback — deliberately distinct from reportCourseIssue
  // (that one is field-level, per-course, and feeds the crowdsource apply
  // pipeline). Writes public.app_feedback; RLS confines the row to its author
  // (+ admin read). category/message are validated here AND by CHECK
  // constraints server-side so a bad client can't widen either. The device
  // context (app_version/platform/locale) is captured by the caller so a
  // report is actionable without a follow-up.
  async function submitFeedback({ userId, category, message, contactEmail, appVersion, platform, locale }) {
    if (!userId) return { error: { message: 'not signed in' } };
    const msg = (message || '').trim();
    if (!msg) return { error: { message: 'message required' } };
    const cat = ['suggestion', 'bug', 'other'].includes(category) ? category : 'suggestion';
    const insert = await sb.from('app_feedback').insert({
      user_id: userId,
      category: cat,
      message: msg,
      contact_email: (contactEmail || '').trim() || null,
      app_version: appVersion || null,
      platform: platform || null,
      locale: locale || null,
    }).select().single();
    if (insert.error) return { error: insert.error };
    return { data: insert.data };
  }

  // ── COMMUNITY SOCIAL (attribution · avatars · reactions) ──────────────────
  // The FIRST client-side RPCs in this file. Their SQL counterparts in
  // supabase-migration-community-social.sql are SECURITY DEFINER — they cross
  // the profiles own-only RLS boundary with minimal disclosure, so they are
  // called via sb.rpc() (not sb.from()). All return { data, error }.

  // Contributors to render on a course detail page. The server resolves each
  // contributor's display per their attribution_visibility and never returns
  // raw user ids. Degrades to [] on error so attribution just disappears.
  async function getCourseContributors(courseId) {
    if (!courseId) return { data: [], error: null };
    const { data, error } = await sb.rpc('get_course_contributors', { p_course_id: String(courseId) });
    if (error) { console.warn('getCourseContributors', error); return { data: [], error }; }
    return { data: data || [], error: null };
  }

  // Per-field provenance for the "who contributed what" dialog. Degrades to []
  // on error so the dialog just shows nothing.
  async function getCourseFieldContributions(courseId) {
    if (!courseId) return { data: [], error: null };
    const { data, error } = await sb.rpc('get_course_field_contributions', { p_course_id: String(courseId) });
    if (error) { console.warn('getCourseFieldContributions', error); return { data: [], error }; }
    return { data: data || [], error: null };
  }

  // Aggregate reaction counts + the caller's own reactions + the live
  // escalation threshold (single source of truth is the SQL side).
  async function getCourseReactionSummary(courseId) {
    if (!courseId) return { data: null, error: null };
    const { data, error } = await sb.rpc('get_course_reaction_summary', { p_course_id: String(courseId) });
    if (error) { console.warn('getCourseReactionSummary', error); return { data: null, error }; }
    return { data, error: null };
  }

  // Toggle the caller's own reaction. Returns the fresh summary + is_upgraded
  // so the caller updates counts optimistically and only refetches the course
  // (to flip its SourceBadge) when is_upgraded is true.
  async function toggleCourseReaction(courseId, reactionType) {
    if (!courseId) return { data: null, error: { message: 'courseId required' } };
    const { data, error } = await sb.rpc('toggle_course_reaction', {
      p_course_id: String(courseId), p_type: reactionType,
    });
    if (error) { console.warn('toggleCourseReaction', error); return { data: null, error }; }
    return { data, error: null };
  }

  // Peer-signal counts for course cards, grouped by repeat key so the editions
  // of a repeating programme pool their history (BLS runs ×21 in the live
  // catalogue; per-course counts put 3 of 133 active courses over the display
  // threshold, rolled up it is 25).
  //
  // ONE call per catalogue load, not one per card: getCourseReactionSummary is
  // per-course, so cards would issue 133 round trips on Discover. Takes no
  // arguments and returns only counts — nothing caller-specific, nothing to
  // poison, so it is safe to call signed-out.
  //
  // Best-effort by design: a database that predates
  // supabase-migration-course-experience.sql 404s this RPC, and revoking its
  // EXECUTE is the documented kill switch. Both land here as "no rollups", and
  // every card then renders no chips rather than an error.
  // ‼️ TWO CALLS, TWO GRAINS, NEVER MERGED. get_course_experience_rollups() —
  // the legacy repeat-key rollup — is deliberately NOT called any more: it
  // pooled every edition of a programme, so 25 of the 28 cards showing a peer
  // number were displaying another sitting's evidence (RELEASE.md §62). The
  // function still EXISTS on the server; retiring it is D2's job, after this
  // client is proven live.
  //
  // ‼️ NO FALLBACK TO IT. If both calls fail the cards show nothing, which is
  // the correct output for evidence we cannot key correctly. Falling back would
  // restore the derived key as evidence authority.
  async function fetchExperienceRollups() {
    const [offering, series] = await Promise.all([
      sb.rpc('get_offering_experience_rollups'),
      sb.rpc('get_series_experience_rollups'),
    ]);
    if (offering.error) console.warn('fetchExperienceRollups(offering)', offering.error);
    if (series.error) console.warn('fetchExperienceRollups(series)', series.error);
    // Judged separately: one grain failing must not blank the other. A card can
    // legitimately show occurrence evidence with no series evidence, and vice
    // versa — they answer different questions.
    if (offering.error && series.error) {
      return { data: null, error: offering.error };
    }
    return {
      data: {
        offering: offering.error ? {} : (offering.data || {}),
        series: series.error ? {} : (series.data || {}),
      },
      error: null,
    };
  }

  // ── COURSE REVIEWS (stars + fixed-vocabulary chips) ───────────────────────
  //
  // ‼️ "THE REVIEW BACKEND ISN'T THERE" IS A NORMAL STATE, NOT AN ERROR.
  // It happens in three ordinary situations and every one of them must render
  // as "reviews are off", never as a database string in front of a nurse:
  //
  //   1. the client shipped ahead of supabase-migration-course-reviews.sql —
  //      which is exactly what happened on 2026-08-11: 我的評價 showed
  //      "Could not find the function public.get_my_reviews without parameters
  //      in the schema cache", in English, on a Chinese page;
  //   2. either documented KILL SWITCH was pulled (revoke execute on
  //      submit_course_review or on the rollups) — RELEASE.md §39/§40 promise
  //      this degrades with no client deploy, and that promise is only true
  //      because of this helper;
  //   3. an older build talking to a newer/rolled-back database.
  //
  // PGRST202 is PostgREST's "no such function in the schema cache"; 42883 is
  // Postgres' own undefined_function; 42501 is insufficient_privilege, which is
  // what a revoke looks like from here.
  const REVIEW_BACKEND_MISSING_CODES = new Set(['PGRST202', '42883', '42501']);
  function reviewBackendMissing(error) {
    if (!error) return false;
    if (REVIEW_BACKEND_MISSING_CODES.has(error.code)) return true;
    // Some transports drop the code and keep only the message.
    const msg = String(error.message || '').toLowerCase();
    return msg.includes('schema cache')
        || msg.includes('does not exist')
        || msg.includes('could not find the function');
  }

  // Upsert the caller's review. Everything that matters is enforced server-side
  // — attendance, the chip vocabulary, the star range, the 30-day edit window —
  // so the error is surfaced RAW for the caller to map:
  //   attendance_required → she has no completed record for this course
  //   edit_window_closed  → past 30 days; the review other nurses have read stands
  //
  // ‼️ TWO CALLS, NOT ONE — AND THE SPLIT IS A SERVER CONTRACT. save_course_rating
  // takes no body parameter at all, so text cannot ride along with a rating
  // save. That is what makes the confirm button real rather than a UI
  // convention. Do not "simplify" these back into one call.
  async function saveCourseRating(courseId, review) {
    if (!courseId) return { data: null, error: { message: 'courseId required' } };
    const { data, error } = await sb.rpc('save_course_rating', {
      p_course_id:      String(courseId),
      p_course_stars:   review?.courseStars ?? null,
      p_provider_stars: review?.providerStars ?? null,
      p_course_chips:   review?.courseChips || [],
      p_provider_chips: review?.providerChips || [],
    });
    if (error) {
      console.warn('saveCourseRating', error);
      return { data: null, error, missing: reviewBackendMissing(error) };
    }
    return { data, error: null };
  }

  // The only path text takes. Every real change re-enters moderation; an
  // unchanged body is a server-side no-op, so reopening the editor cannot
  // re-queue an approved comment.
  async function submitCourseReviewBody(courseId, body) {
    if (!courseId) return { data: null, error: { message: 'courseId required' } };
    const { data, error } = await sb.rpc('submit_course_review_body', {
      p_course_id: String(courseId),
      p_body:      body ?? null,
    });
    if (error) {
      console.warn('submitCourseReviewBody', error);
      return { data: null, error, missing: reviewBackendMissing(error) };
    }
    return { data, error: null };
  }

  // Taking down your own words is not editing, so this has no 30-day window.
  async function withdrawCourseReview(courseId) {
    if (!courseId) return { data: null, error: { message: 'courseId required' } };
    const { data, error } = await sb.rpc('withdraw_course_review', {
      p_course_id: String(courseId),
    });
    if (error) {
      console.warn('withdrawCourseReview', error);
      return { data: null, error, missing: reviewBackendMissing(error) };
    }
    return { data, error: null };
  }

  // Published review bodies for a course's repeat-key group. Signed-in only by
  // ACL; returns no author, and no chips (a chip printed beside one anonymous
  // comment reads as a specific accusation — chips belong in the aggregate).
  async function fetchCourseReviewsPublic(courseId, opts) {
    if (!courseId) return { data: [], error: null };
    const { data, error } = await sb.rpc('get_course_reviews_public', {
      p_course_id: String(courseId),
      p_limit:     opts?.limit ?? 20,
      p_before_at: opts?.beforeAt ?? null,
      p_before_id: opts?.beforeId ?? null,
    });
    if (error) {
      console.warn('fetchCourseReviewsPublic', error);
      return { data: null, error, missing: reviewBackendMissing(error) };
    }
    return { data: Array.isArray(data) ? data : [], error: null };
  }

  // Report / block take a REVIEW id — never an author id. The server resolves
  // the author internally so the client never learns who wrote what, which is
  // the whole basis of review anonymity.
  async function reportCourseReview(reviewId, reason) {
    const { data, error } = await sb.rpc('report_course_review', {
      p_review_id: reviewId, p_reason: reason || null,
    });
    if (error) { console.warn('reportCourseReview', error); return { data: null, error }; }
    return { data, error: null };
  }

  async function blockReviewAuthor(reviewId) {
    const { data, error } = await sb.rpc('block_review_author', { p_review_id: reviewId });
    if (error) { console.warn('blockReviewAuthor', error); return { data: null, error }; }
    return { data, error: null };
  }

  // All-or-nothing: a per-author unblock list would leak the identities the
  // review surface hides.
  async function clearReviewAuthorBlocks() {
    const { data, error } = await sb.rpc('clear_review_author_blocks');
    if (error) { console.warn('clearReviewAuthorBlocks', error); return { data: null, error }; }
    return { data, error: null };
  }

  // ‼️ DEPRECATED — the pre-split combined call. Kept only so an older cached
  // bundle keeps working; new code must use saveCourseRating +
  // submitCourseReviewBody so the confirm step stays meaningful.
  async function submitCourseReview(courseId, review) {
    if (!courseId) return { data: null, error: { message: 'courseId required' } };
    const { data, error } = await sb.rpc('submit_course_review', {
      p_course_id:      String(courseId),
      p_course_stars:   review?.courseStars ?? null,
      p_provider_stars: review?.providerStars ?? null,
      p_course_chips:   review?.courseChips || [],
      p_provider_chips: review?.providerChips || [],
      p_body:           review?.body ?? null,
    });
    if (error) {
      console.warn('submitCourseReview', error);
      return { data: null, error, missing: reviewBackendMissing(error) };
    }
    return { data, error: null };
  }

  // The caller's own review, for rendering the edit state. Own-row only,
  // server-side — there is deliberately no way to read anyone else's.
  async function fetchMyCourseReview(courseId) {
    if (!courseId) return { data: null, error: null };
    const { data, error } = await sb.rpc('get_my_course_review', { p_course_id: String(courseId) });
    if (error) {
      console.warn('fetchMyCourseReview', error);
      return { data: null, error, missing: reviewBackendMissing(error) };
    }
    return { data: data && Object.keys(data).length ? data : null, error: null };
  }

  // Every review the caller has written, keyed by course id. ONE call for the
  // whole 已完成紀錄 list — asking per row would be an N+1 across the history.
  async function fetchMyCourseReviews() {
    const { data, error } = await sb.rpc('get_my_course_reviews');
    if (error) {
      console.warn('fetchMyCourseReviews', error);
      return { data: null, error, missing: reviewBackendMissing(error) };
    }
    return { data: data || {}, error: null };
  }

  // The author's full review history, for Profile → 我的評價. Own rows only,
  // server-side. Includes bodies, statuses and any moderator note — this is
  // what makes "saved privately first" honest rather than a black hole.
  async function fetchMyReviews() {
    const { data, error } = await sb.rpc('get_my_reviews');
    if (error) {
      console.warn('fetchMyReviews', error);
      return { data: null, error, missing: reviewBackendMissing(error) };
    }
    return { data: Array.isArray(data) ? data : [], error: null };
  }

  // Public review aggregates at BOTH grains — what this course is like across
  // its runs (series), and what that one sitting was like (offering). One call
  // each per catalogue load, same reasoning as fetchExperienceRollups: the
  // per-course shape would be 133 round trips.
  //
  // ‼️ The thresholds live on the SERVER. A mean below n=5 is not sent at all,
  // so there is nothing here the client could accidentally render — do not add
  // a client-side "if (n >= 5)" and assume it is the only guard.
  //
  // ‼️ Both calls are issued together but judged separately: if only one is
  // missing (a half-applied backend), the other's numbers are still correct, so
  // we degrade to fewer numbers rather than to none. `missing` is reported only
  // when the SERIES call fails, because that is the one the cards depend on.
  async function fetchReviewRollups() {
    const [series, offering] = await Promise.all([
      sb.rpc('get_series_review_rollups'),
      sb.rpc('get_offering_review_rollups'),
    ]);
    if (series.error) {
      console.warn('fetchReviewRollups(series)', series.error);
      return { data: null, error: series.error, missing: reviewBackendMissing(series.error) };
    }
    if (offering.error) {
      // Not fatal: occurrence numbers vanish, series numbers stay.
      console.warn('fetchReviewRollups(offering)', offering.error);
    }
    return {
      data: { series: series.data || {}, offering: offering.error ? {} : (offering.data || {}) },
      error: null,
    };
  }

  // Set/clear the caller's community handle. Format + reserved-word + case-
  // insensitive uniqueness are enforced server-side; pass null/'' to clear.
  // Surfaces the raw error so the settings UI can show the friendly message.
  async function setCommunityHandle(handle) {
    const { data, error } = await sb.rpc('set_community_handle', { p_handle: handle || null });
    if (error) { console.warn('setCommunityHandle', error); return { data: null, error }; }
    return { data, error: null };
  }

  // Report an inappropriate community handle. The reported handle is resolved
  // to a user id SERVER-SIDE, so the client never learns any contributor id.
  async function reportInappropriateHandle(courseId, handle, reason) {
    const { data, error } = await sb.rpc('report_inappropriate_handle', {
      p_course_id: String(courseId), p_handle: handle, p_reason: reason || null,
    });
    if (error) { console.warn('reportInappropriateHandle', error); return { data: null, error }; }
    return { data, error: null };
  }

  // Lightweight probe for the one-time "pick a handle" invite (app.jsx D2):
  // does this user have ≥1 approved submission OR ≥1 applied report? Two
  // limit(1) queries — cheap enough to run on app resume.
  async function hasApprovedContribution(userId) {
    if (!userId) return false;
    const [subRes, repRes] = await Promise.all([
      sb.from('course_submissions').select('id')
        .eq('submitter_user_id', userId).eq('status', 'approved').limit(1),
      sb.from('course_reports').select('id')
        .eq('reporter_user_id', userId).not('applied_at', 'is', null).limit(1),
    ]);
    return !!((subRes.data && subRes.data.length) || (repRes.data && repRes.data.length));
  }

  // ── CROWDSOURCE ADMIN (Phase 4) ───────────────────────────────────────────
  // In-app admin UI helpers. The UI only renders these for users where
  // isAdmin(profile) returns true — but RLS + RPC role checks also gate
  // access server-side, so the visibility flag is purely a UX affordance,
  // never the security boundary.

  function isAdmin(profile) {
    return !!profile && (profile.role === 'admin' || profile.role === 'moderator');
  }

  // ‼️ STRICTER THAN isAdmin ON PURPOSE — 'admin' only, never 'moderator'.
  // Mirrors public.is_full_admin() in SQL and the role check in the
  // admin-notices Edge Function; all three must change together. Used to decide
  // whether the 公告 tab renders at all. Everywhere else in this app "admin"
  // includes moderators, because everywhere else the power is over one queue
  // item; publishing a notice writes to every nurse's dashboard at once.
  //
  // Rendering only. profiles.role is a server-pinned mirror (profiles_pin_role
  // trigger) and is NOT the security boundary — the RPCs re-check auth.uid()
  // against admin_users and return 42501 regardless of what the client believes.
  function isFullAdmin(profile) {
    return !!profile && profile.role === 'admin';
  }

  // Soft pagination boundary for the admin review queues.
  //
  // (1) Technical: the Phase 4 v1 UI loads all matching rows at once (no
  //     .range() / cursor). Fine for small queues; past ~50 rows it becomes
  //     slow + DOM-heavy + easy to lose context while scrolling.
  //
  // (2) Product: hitting this threshold means the community is producing
  //     submissions/reports faster than a single admin can review on a
  //     casual cadence. The right product response is twofold —
  //       a. add cursor pagination (technical),
  //       b. recruit a second reviewer and promote them to 'moderator'
  //          through the server-controlled admin_users role path,
  //     so review throughput scales with contribution throughput rather
  //     than capping at one person's bandwidth.
  //
  // See memory: cap-signals-are-product-signals — when a threshold has
  // an operator-facing trigger, write the product reaction next to the
  // technical reason at definition time.
  const PAGINATION_WARN_THRESHOLD = 40;

  function checkPaginationWarning(fnName, rows) {
    const n = rows?.length || 0;
    if (n >= PAGINATION_WARN_THRESHOLD) {
      console.warn(
        `[${fnName}] returned ${n} rows — at or above PAGINATION_WARN_THRESHOLD (${PAGINATION_WARN_THRESHOLD}). ` +
        `Time to add cursor pagination to this fetch helper + the matching AdminScreen tab.`
      );
      return true;
    }
    return false;
  }

  // Pending submissions for the review queue. RLS lets admin/moderator
  // read all rows; non-admin callers will see only their own (which is
  // why the UI guards on isAdmin before showing the page).
  // Submissions by the SAME submitters as the current queue, in any status.
  //
  // ‼️ THE PENDING QUEUE IS THE WRONG CORPUS FOR DUPLICATE REVIEW, and the most
  // important live case proves it: the 「Lase→Last」 pair is one APPROVED row and
  // one PENDING row. Comparing only within the queue would have missed the
  // single case the batch was built for.
  //
  // Scoped to the submitters already on screen rather than the whole table:
  // Stage 1 requires an identical submitter, so no other row can ever pair, and
  // this stays one small query for the whole queue instead of one per card.
  // Admins may read every status ("submissions admin all" in
  // supabase-migration-authz-hardening.sql), so nothing here widens access.
  async function fetchDuplicateCandidates(submitterIds) {
    const ids = [...new Set((submitterIds || []).filter(Boolean))];
    if (!ids.length) return { data: [], error: null };
    const { data, error } = await sb.from('course_submissions')
      .select('id, status, submitter_user_id, payload, payload_schema_version, approved_course_id, created_at')
      .in('submitter_user_id', ids)
      .order('created_at', { ascending: true });
    if (error) {
      // A duplicate HINT is not worth breaking the review queue over.
      console.warn('fetchDuplicateCandidates', error);
      return { data: [], error };
    }
    return {
      data: (data || []).map(row => ({
        ...row,
        payload: normaliseSubmissionPayload(row.payload, row.payload_schema_version),
      })),
      error: null,
    };
  }

  async function fetchPendingSubmissions() {
    const { data, error } = await sb.from('course_submissions')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: true });
    if (error) { console.warn('fetchPendingSubmissions', error); return { data: null, error }; }
    const paginationWarning = checkPaginationWarning('fetchPendingSubmissions', data);
    return {
      data: (data || []).map(row => ({
        ...row,
        payload: normaliseSubmissionPayload(row.payload, row.payload_schema_version),
      })),
      error: null,
      paginationWarning,
    };
  }

  async function fetchPendingReports() {
    const { data, error } = await sb.from('course_reports')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: true });
    if (error) { console.warn('fetchPendingReports', error); return { data: null, error }; }
    const paginationWarning = checkPaginationWarning('fetchPendingReports', data);
    return { data: data || [], error: null, paginationWarning };
  }

  // Accepted-but-not-yet-applied reports — the apply queue. Admin reviews
  // these to decide whether to actually patch cne_courses and with what
  // value (which may differ from the reporter's suggestion).
  async function fetchAcceptedReports() {
    const { data, error } = await sb.from('course_reports')
      .select('*')
      .eq('status', 'accepted')
      .is('applied_at', null)
      .order('reviewed_at', { ascending: true });
    if (error) { console.warn('fetchAcceptedReports', error); return { data: null, error }; }
    const paginationWarning = checkPaginationWarning('fetchAcceptedReports', data);
    return { data: data || [], error: null, paginationWarning };
  }

  // All user feedback for the admin inbox (Profile → Leave a comment feeds
  // this). RLS lets admin/moderator read every row; a non-admin caller would
  // see only their own, which is why the UI gates on isAdmin. Newest first —
  // this is an inbox, not a FIFO work queue like submissions/reports.
  async function fetchAllFeedback() {
    const { data, error } = await sb.from('app_feedback')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) { console.warn('fetchAllFeedback', error); return { data: null, error }; }
    const paginationWarning = checkPaginationWarning('fetchAllFeedback', data);
    return { data: data || [], error: null, paginationWarning };
  }

  // Triage a feedback row: 'new' → 'reviewing' → 'resolved' | 'archived'
  // (and back). RLS confines this to admin/moderator. We stamp reviewed_at /
  // reviewed_by from the current session so the inbox shows who last actioned
  // an item. The CHECK constraint on status is the server-side backstop.
  async function updateFeedbackStatus(id, status) {
    if (!id) return { error: { message: 'missing id' } };
    if (!['new', 'reviewing', 'resolved', 'archived'].includes(status)) {
      return { error: { message: 'invalid status' } };
    }
    let reviewedBy = null;
    try {
      const { data: { session } = {} } = await sb.auth.getSession();
      reviewedBy = session?.user?.id || null;
    } catch (_) { /* reviewer stamp is best-effort */ }
    const { data, error } = await sb.from('app_feedback')
      .update({ status, reviewed_at: new Date().toISOString(), reviewed_by: reviewedBy })
      .eq('id', id)
      .select()
      .single();
    if (error) { console.warn('updateFeedbackStatus', error); return { error }; }
    return { data };
  }

  // Resolve user_id → display name for a batch of ids (feedback inbox shows
  // who left each comment). Admins can read all profiles (see fetchAdmin
  // Profiles); a non-admin caller is RLS-limited to their own row, so this
  // never leaks names outside the admin surface. Returns Map<id, name|null>;
  // degrades to an empty Map on error so the inbox still renders.
  async function fetchProfileNamesBatch(userIds) {
    const ids = [...new Set((userIds || []).filter(Boolean))];
    if (!ids.length) return new Map();
    const { data, error } = await sb.from('profiles')
      .select('id, display_name, name_en, name_zh, short_name_en, short_name_zh')
      .in('id', ids);
    if (error) { console.warn('fetchProfileNamesBatch', error); return new Map(); }
    const m = new Map();
    for (const p of (data || [])) {
      m.set(p.id, p.display_name || p.name_en || p.name_zh || p.short_name_en || p.short_name_zh || null);
    }
    return m;
  }

  // Fresh locked_fields for the courses referenced by the admin review queues.
  // Returns Map<courseId, string[]> (column names an admin has corrected).
  //
  // Read fresh from cne_courses_v rather than the in-memory COURSES cache:
  // the highest-value case is a same-session duplicate (admin applies a
  // correction to a field, then reviews a second report on the SAME field) —
  // the cache is stale there, but AdminScreen.refresh() re-runs this after
  // every apply, so the lock shows up immediately.
  //
  // Best-effort: if locked_fields doesn't exist yet (migration not run) the
  // PostgREST select errors; we swallow it and return an empty map so the
  // admin UI degrades to "no lock notices" instead of breaking. Lets the
  // client ship before/after the migration without coordination.
  async function fetchLockedFieldsForCourses(courseIds) {
    if (!Array.isArray(courseIds) || courseIds.length === 0) return new Map();
    const uniqueIds = [...new Set(courseIds.filter(Boolean).map(String))];
    if (uniqueIds.length === 0) return new Map();
    const m = new Map();
    try {
      const { data, error } = await sb.from('cne_courses_v')
        .select('id, locked_fields')
        .in('id', uniqueIds);
      if (error) { console.warn('fetchLockedFieldsForCourses', error); return m; }
      for (const row of (data || [])) {
        m.set(String(row.id), Array.isArray(row.locked_fields) ? row.locked_fields : []);
      }
    } catch (e) {
      console.warn('fetchLockedFieldsForCourses (non-fatal)', e?.message || e);
    }
    return m;
  }

  // Batch-fetch contributor stats for the unique submitter/reporter ids
  // visible on the current admin page. Two parallel queries + JS-side
  // group-by → one round-trip per table, instead of N per card.
  //
  // Returns Map<userId, {
  //   submissions: { total, pending, approved, rejected, duplicate },
  //   reports:     { total, pending, accepted, rejected },
  // }>
  //
  // Any user id not represented in either table maps to all-zero counts
  // so the UI doesn't have to null-check.
  async function fetchContributorStatsBatch(userIds) {
    if (!Array.isArray(userIds) || userIds.length === 0) return new Map();
    const uniqueIds = [...new Set(userIds.filter(Boolean))];
    if (uniqueIds.length === 0) return new Map();

    const [subRes, repRes] = await Promise.all([
      sb.from('course_submissions')
        .select('submitter_user_id, status')
        .in('submitter_user_id', uniqueIds),
      sb.from('course_reports')
        .select('reporter_user_id, status')
        .in('reporter_user_id', uniqueIds),
    ]);
    if (subRes.error) console.warn('fetchContributorStatsBatch submissions', subRes.error);
    if (repRes.error) console.warn('fetchContributorStatsBatch reports', repRes.error);

    const m = new Map();
    for (const id of uniqueIds) {
      m.set(id, {
        submissions: { total: 0, pending: 0, approved: 0, rejected: 0, duplicate: 0 },
        reports:     { total: 0, pending: 0, accepted: 0, rejected: 0 },
      });
    }
    for (const r of (subRes.data || [])) {
      const s = m.get(r.submitter_user_id);
      if (!s) continue;
      s.submissions.total++;
      if (s.submissions[r.status] !== undefined) s.submissions[r.status]++;
    }
    for (const r of (repRes.data || [])) {
      const s = m.get(r.reporter_user_id);
      if (!s) continue;
      s.reports.total++;
      if (s.reports[r.status] !== undefined) s.reports[r.status]++;
    }
    return m;
  }

  // Full history for a single contributor — used by the click-through
  // dialog from a card's stats line. Returns submissions + reports as
  // separate arrays (each newest-first), capped at 100 rows per side.
  //
  // Hitting the cap is BOTH a technical signal AND a product signal:
  //   - Technical: paginate this query before the cap actually clips data
  //   - Product:   this is one of the platform's most active contributors;
  //                worth promoting to a higher trust tier (Phase 3) and
  //                worth a closer look on whatever they submit now.
  // The dialog renders the product framing as a positive banner so the
  // admin can act on it; this function surfaces the cap booleans for that.
  // See memory: cap-signals-are-product-signals.
  const HISTORY_CAP = 100;
  async function fetchContributorHistory(userId) {
    if (!userId) return { submissions: [], reports: [], submissionsAtCap: false, reportsAtCap: false };
    const [subRes, repRes] = await Promise.all([
      sb.from('course_submissions')
        .select('id, status, payload, created_at, reviewed_at, admin_notes, approved_course_id, source_origin')
        .eq('submitter_user_id', userId)
        .order('created_at', { ascending: false })
        .limit(HISTORY_CAP),
      sb.from('course_reports')
        .select('id, status, course_id, field_path, current_value, suggested_value, reason, created_at, reviewed_at, applied_at, applied_value, admin_notes')
        .eq('reporter_user_id', userId)
        .order('created_at', { ascending: false })
        .limit(HISTORY_CAP),
    ]);
    if (subRes.error) console.warn('fetchContributorHistory submissions', subRes.error);
    if (repRes.error) console.warn('fetchContributorHistory reports', repRes.error);
    const subs = subRes.data || [];
    const reps = repRes.data || [];
    const submissionsAtCap = subs.length >= HISTORY_CAP;
    const reportsAtCap     = reps.length >= HISTORY_CAP;
    if (submissionsAtCap || reportsAtCap) {
      console.warn(
        `[fetchContributorHistory] user=${userId} hit history cap (${HISTORY_CAP}). ` +
        `This is a top-tier contributor — paginate this fetch AND review them for elevated trust.`
      );
    }
    return {
      submissions: subs.map(row => ({
        ...row,
        // Re-normalise payload through the schema-versioned helper so the
        // dialog always sees the current shape regardless of when the
        // submission was stored.
        payload: row.payload ? normaliseSubmissionPayload(row.payload, 1) : null,
      })),
      reports: reps,
      submissionsAtCap,
      reportsAtCap,
    };
  }

  // Signed URL for an evidence file. Path is stored in evidence_urls[]
  // already-namespaced as `<userId>/<owningId>/<filename>`, so callers
  // pass it through unchanged. 10-minute expiry matches certificates.
  async function getSubmissionEvidenceUrl(path) {
    if (!path) return null;
    const { data } = await sb.storage.from('submissions').createSignedUrl(path, 60 * 10);
    return data?.signedUrl || null;
  }

  // ── ADMIN ROLE MANAGEMENT ────────────────────────────────────────────────
  // The server returns a minimal admin roster from public.admin_users; client
  // code must never read or write profiles.role as an authorization source.
  async function fetchAdminProfiles() {
    const { data, error } = await adminCallEdge('admin-roles', { action: 'list' });
    if (error) { console.warn('fetchAdminProfiles', error); return { data: null, error }; }
    return { data: data?.data || [], error: null };
  }

  // Search remains server-side so the role panel gets only its minimal,
  // deliberate projection instead of a broad profiles SELECT policy.
  async function searchProfiles(query) {
    const q = (query || '').trim();
    if (!q) return { data: [], error: null };
    const { data, error } = await adminCallEdge('admin-roles', { action: 'search', query: q });
    if (error) { console.warn('searchProfiles', error); return { data: null, error }; }
    return { data: data?.data || [], error: null };
  }

  // The SECURITY DEFINER RPC applies the self-change and last-admin guards,
  // then maintains profiles.role as a legacy read-only UI mirror.
  async function setProfileRole(userId, role) {
    if (!userId) return { error: { message: 'missing user id' } };
    const { data, error } = await adminCallEdge('admin-roles', {
      action: 'setRole', targetId: userId, role: role || null,
    });
    if (error) { console.warn('setProfileRole', error); return { data: null, error }; }
    return { data: data?.data || [], error: null };
  }

  // Generic admin Edge Function caller. All five admin endpoints share the
  // same auth + JSON shape; this wraps the fetch + standard error mapping
  // (the Edge Functions already return useful {error, code} on failure).
  const FUNCTIONS_BASE = 'https://iyrxakaiowiilphxzyuq.supabase.co/functions/v1';

  // Refresh proactively when the stored token has under this long left.
  const ADMIN_TOKEN_REFRESH_MARGIN_S = 60;

  // getSession() only refreshes a token that has ALREADY expired, and it
  // compares expires_at against the *device* clock with no margin — so on
  // its own it hands back tokens that are seconds from death (or already
  // dead by the server's clock). Refresh first when we're inside the margin
  // so the common "aged token" case costs one round trip, not a failed call
  // plus a retry. Device-clock skew can still slip a "fresh"-looking token
  // past this check; that's caught by the 401 retry in adminCallEdge.
  async function adminFreshToken() {
    const { data: { session } } = await sb.auth.getSession();
    if (!session?.access_token) return null;
    const expiresSoon =
      (session.expires_at ?? Infinity) <= Date.now() / 1000 + ADMIN_TOKEN_REFRESH_MARGIN_S;
    if (expiresSoon) {
      const { data, error } = await sb.auth.refreshSession();
      if (!error && data?.session?.access_token) return data.session.access_token;
    }
    return session.access_token;
  }

  async function adminCallEdge(fnName, body, _retried = false) {
    const accessToken = await adminFreshToken();
    if (!accessToken) return { error: { message: 'not signed in' } };
    let resp;
    try {
      resp = await fetch(`${FUNCTIONS_BASE}/${fnName}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body || {}),
      });
    } catch (e) {
      return { error: { message: `Network error: ${e?.message || e}` } };
    }
    // A 401 means the Edge Function rejected our token at its auth gate,
    // which runs BEFORE any RPC — so nothing was written and retrying is
    // side-effect-free. We can land here even though adminFreshToken()
    // trusted the token: the server validates the JWT's real exp against its
    // OWN clock, so device clock skew (or a WebView whose 30s auto-refresh
    // timer was suspended while backgrounded) makes a locally-"valid" token
    // read as expired server-side. refreshSession() mints a brand-new token
    // independent of the device clock, so retry exactly once with it.
    // INVARIANT: this retry is only safe while the auth check stays first in
    // every admin Edge Function (approve/reject-submission,
    // accept/reject/apply-report). Don't move a write ahead of it.
    if (resp.status === 401 && !_retried) {
      const { data, error } = await sb.auth.refreshSession();
      if (!error && data?.session?.access_token) {
        return adminCallEdge(fnName, body, true);
      }
    }
    let result = null;
    try { result = await resp.json(); } catch (_) { /* empty body */ }
    if (!resp.ok) {
      const code = result?.code || null;
      return { error: {
        // `reason` is the MACHINE-READABLE half: a stable slug the caller can
        // branch on (e.g. 'identity_disposition_required' opens the identity
        // picker instead of just toasting). `message` stays the human half.
        // Added because the approve path needs to distinguish "blocked pending
        // a verdict" — retryable, with a concrete next action — from "not
        // found", and those two arrive under the same SQLSTATE.
        reason: result?.reason || null,
        detail: result?.detail || null,
        status: resp.status,
        // Carry the error code INLINE in the message. These endpoints
        // deliberately return a sanitized public string ("Approval failed")
        // instead of the raw Postgres message, and the admin toast renders
        // `message` only — so a real failure arrived on the phone with zero
        // diagnostic content. That cost a full round trip on 2026-08-04, when
        // "失敗: Approval failed" was really SQLSTATE 42702. This is an
        // admin-only call path: a bare SQLSTATE leaks no schema. Do NOT reuse
        // this shape on user-facing screens.
        message: (result?.error || `HTTP ${resp.status}`) + (code ? ` (${code})` : ''),
        code,
      } };
    }
    return { data: result };
  }

  // ⚠️ `identityDisposition` IS THE ADMIN'S §76 VERDICT AND MUST BE FORWARDED.
  // A community approval whose provider has no canonical alias contract cannot
  // be resolved mechanically; the resolver refuses rather than guessing. Passing
  // undefined means "resolve mechanically or refuse" — which is right when the
  // contract layer CAN answer, and is exactly what the admin card sends until
  // the reviewer picks something. See AdminSubmissionCard in screens.jsx.
  // ⚠️ `expectedRevision` IS THE REVISION THE ADMIN ACTUALLY SAW.
  //
  // ‼️ NEVER RE-READ IT HERE. It would be trivial — and completely wrong — to
  // fetch the row's current revision at call time and send that: the check would
  // then always compare a value to itself and pass, and the optimistic-concurrency
  // contract would be theatre. The whole point is to detect that the submitter
  // edited the row BETWEEN the card rendering and the reviewer tapping 通過, so
  // the value must travel down from the render that produced the pixels.
  //
  // Null means "no revision was reviewed" and the server skips the check —
  // correct for older admin builds that do not send it (same deploy-ahead-of-
  // client rule as reviewedPayload), and the reason this can ship before the
  // pending-edit activation without changing any current behaviour.
  const adminApproveSubmission = (submissionId, adminNotes, reviewedPayload, identityDisposition, expectedRevision) =>
    adminCallEdge('approve-submission', {
      submissionId, adminNotes, reviewedPayload,
      identityDisposition: identityDisposition || null,
      expectedRevision: Number.isInteger(expectedRevision) ? expectedRevision : null,
    });
  // Read-only candidate lists behind the identity picker. provider/series/
  // offering_entities carry no `authenticated` grants (service-role reads by
  // design), so this cannot be a direct PostgREST query from the client.
  const adminIdentityCandidates = (body) =>
    adminCallEdge('identity-candidates', body);
  // Read-only suggestions for a submission's official / registration page, each
  // with the evidence its page shows (find-registration-link, §156). Nothing is
  // written: the admin card copies a link into its reviewed payload only when
  // the reviewer taps it.
  const adminFindRegistrationLink = (body) =>
    adminCallEdge('find-registration-link', body);
  const adminRejectSubmission = (submissionId, adminNotes) =>
    adminCallEdge('reject-submission', { submissionId, adminNotes });
  const adminAcceptReport = (reportId, adminNotes) =>
    adminCallEdge('accept-report', { reportId, adminNotes });
  const adminRejectReport = (reportId, adminNotes) =>
    adminCallEdge('reject-report', { reportId, adminNotes });
  const adminApplyReport = (reportId, applyValue, justification) =>
    adminCallEdge('apply-report', { reportId, applyValue, justification });
  // Post a user-facing reply to a comment (app_feedback). The Edge Function
  // sets admin_reply via reply_to_feedback (admin-checked) AND notifies the
  // author (in-app + push). status is 'resolved' (default) or 'reviewing'.
  const adminReplyToFeedback = (feedbackId, reply, status = 'resolved') =>
    adminCallEdge('reply-feedback', { feedbackId, reply, status });
  // Review moderation. Both verbs go through ONE Edge Function because they
  // share a screen and an authorisation; the underlying RPCs are service_role
  // only, so there is no client path that skips the JWT check.
  const adminListPendingReviews = () =>
    adminCallEdge('moderate-review', { action: 'list' });
  // Suspend the AUTHOR of a review, addressed by review id so no admin surface
  // has to hold a user id to do its job.
  const adminSuspendReviewAuthor = (reviewId, suspend, reason) =>
    adminCallEdge('moderate-review', { action: 'suspend', reviewId, suspend, reason });
  const adminModerateReview = (reviewId, decision, adminNotes) =>
    adminCallEdge('moderate-review', { action: 'moderate', reviewId, decision, adminNotes });

  // ── AI certificate scan ────────────────────────────────────────────────────
  // Multipart variant of adminCallEdge (which only sends JSON). Same fresh-
  // token + single-401-retry contract; extract-certificate keeps its auth
  // gate BEFORE the credit consume, so the retry stays side-effect-free —
  // the adminCallEdge INVARIANT comment applies here too.
  // Returns { data: { extraction, creditsRemaining } } | { error: { message, code } }.
  const EXTRACT_TIMEOUT_MS = 110_000; // server aborts Vertex at 90s; leave headroom

  // `input` is a File (multipart upload) OR a plain object { url } — the
  // edge function fetches the page server-side (course poster / announcement
  // link) and runs the same extraction.
  // opts.signal (optional AbortSignal) lets the progress dialog's 取消 button
  // genuinely abort the request — a cancelled call resolves with
  // { error: { code: 'cancelled' } } so callers can stay silent (no toast).
  async function extractCertificate(input, opts = {}) {
    const externalSignal = opts.signal || null;
    const _retried = !!opts._retried;
    const accessToken = await adminFreshToken();
    if (!accessToken) return { error: { message: 'not signed in' } };
    if (externalSignal?.aborted) return { error: { message: 'cancelled', code: 'cancelled' } };
    const isUrl = input && typeof input === 'object' && typeof input.url === 'string';
    // opts.purpose: 'submission' = community-contribution scan → FREE
    // (server skips the credit consume); anything else = personal log scan.
    const purpose = opts.purpose === 'submission' ? 'submission' : 'log';
    let body, headers;
    if (isUrl) {
      body = JSON.stringify({ url: input.url, purpose });
      headers = { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' };
    } else {
      // NOTE: no Content-Type header — the browser sets the multipart boundary.
      const fd = new FormData();
      fd.append('file', input, input.name || 'certificate');
      fd.append('purpose', purpose);
      body = fd;
      headers = { Authorization: `Bearer ${accessToken}` };
    }
    // Combine the caller's cancel signal with the client-side timeout.
    // AbortSignal.timeout needs iOS 16+ WebView; older ones just get no
    // client-side timeout (the server still aborts Vertex at 90s).
    let signal;
    if (externalSignal && typeof AbortController !== 'undefined') {
      const ctl = new AbortController();
      externalSignal.addEventListener('abort', () => ctl.abort(), { once: true });
      const timer = setTimeout(() => ctl.abort(), EXTRACT_TIMEOUT_MS);
      ctl.signal.addEventListener('abort', () => clearTimeout(timer), { once: true });
      signal = ctl.signal;
    } else if (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) {
      signal = AbortSignal.timeout(EXTRACT_TIMEOUT_MS);
    }
    let resp;
    try {
      resp = await fetch(`${FUNCTIONS_BASE}/extract-certificate`, {
        method: 'POST',
        headers,
        body,
        signal,
      });
    } catch (e) {
      if (externalSignal?.aborted) {
        return { error: { message: 'cancelled', code: 'cancelled' } };
      }
      return { error: { message: `Network error: ${e?.message || e}` } };
    }
    if (resp.status === 401 && !_retried) {
      const { data, error } = await sb.auth.refreshSession();
      if (!error && data?.session?.access_token) {
        return extractCertificate(input, { ...opts, _retried: true });
      }
    }
    let result = null;
    try { result = await resp.json(); } catch (_) { /* empty body */ }
    if (!resp.ok) {
      return { error: {
        message: result?.error || `HTTP ${resp.status}`,
        code: result?.code || null,
        status: result?.status || null, // quota snapshot on quota_exhausted
      } };
    }
    return { data: result };
  }

  // Quality-loop breadcrumb (PRIVACY.md §5 contract: field NAMES only, never
  // contents). Fire-and-forget from LogSheet after a save that used an AI
  // pre-fill; failures are silent — metrics must never block logging.
  async function logAiScanFeedback({ refId = null, isCert = null, docType = null, aiFilledFields = [], editedFields = [] } = {}) {
    try {
      const { data: { session } } = await sb.auth.getSession();
      const uid = session?.user?.id;
      if (!uid) return;
      await sb.from('ai_scan_feedback').insert({
        user_id: uid, ref_id: refId, is_cne_certificate: isCert,
        doc_type: docType,
        ai_filled_fields: aiFilledFields, edited_fields: editedFields,
      });
    } catch (e) { console.warn('logAiScanFeedback (non-fatal)', e?.message || e); }
  }

  // Per-channel monthly earn progress for the "earn credits" menu. Caps live
  // server-side (scan_credit_config); this returns {submission,correction,share}
  // each {earned, cap, per}. null on any error → UI hides the progress line.
  async function getScanEarnStatus() {
    try {
      const { data: { session } } = await sb.auth.getSession();
      const uid = session?.user?.id;
      if (!uid) return null;
      const { data, error } = await sb.rpc('scan_earn_status', { p_user_id: uid });
      if (error) { console.warn('getScanEarnStatus', error); return null; }
      return data || null;
    } catch (e) { console.warn('getScanEarnStatus', e); return null; }
  }

  // Admin → AI 掃描 dashboard aggregates. SECURITY DEFINER RPC; assert_admin
  // runs server-side on auth.uid(), so a non-admin gets a 42501 error here.
  async function adminScanStats() {
    const { data, error } = await sb.rpc('admin_scan_stats');
    if (error) { console.warn('adminScanStats', error); return { error }; }
    return { data };
  }

  // ── PRO ENTITLEMENT (subscription) ────────────────────────────────────────
  //
  // ‼️ THE CLIENT IS NOT THE AUTHORITY. Everything here is for RENDERING only —
  // which price card to show, whether to say "unlimited". The decision that
  // actually spends money (skipping consume_scan_credit before a Vertex AI
  // call) is made server-side by is_pro() inside the extract-certificate edge
  // function. If you ever find yourself passing an `isPro` flag INTO a
  // server call, stop: that is the shape of the bug this split exists to
  // prevent.
  //
  // RLS on `entitlements` is owner-SELECT-only, so this read is safe and a
  // tampered client can only lie to itself.
  //
  // LAPSE RULE (see supabase-migration-entitlements.sql): losing Pro must
  // never lock a nurse out of her own records, certificates or Annex A + B
  // exports. Pro may offer an audit-pack convenience, never access to the
  // underlying evidence. No caller of isPro() may gate a read, export or delete.
  async function getEntitlement() {
    try {
      const { data: { session } } = await sb.auth.getSession();
      const uid = session?.user?.id;
      if (!uid) return { pro: false, status: null };
      const { data, error } = await sb.from('entitlements')
        .select('status, expires_at, product_id, promo_until')
        .eq('user_id', uid).maybeSingle();
      if (error) { console.warn('getEntitlement', error); return { pro: false, status: null }; }
      if (!data) return { pro: false, status: null };
      // ‼️ TWO INDEPENDENT SOURCES, exactly as public.is_pro() computes it. An
      // access code grants Pro through promo_until and never touches the Apple
      // columns, so reading only `status` would render the free tier to someone
      // the SERVER already treats as a subscriber — the worst kind of
      // disagreement, because every gate would open except the UI.
      const paid = ['active', 'grace'].includes(data.status)
        && (!data.expires_at || Date.parse(data.expires_at) > Date.now());
      const promo = !!data.promo_until && Date.parse(data.promo_until) > Date.now();
      return {
        pro: paid || promo,
        status: data.status,
        expiresAt: data.expires_at,
        productId: data.product_id,
        // Which source is carrying her right now — the membership sheet says
        // different things about renewal depending on the answer.
        source: paid ? 'apple' : (promo ? 'promo' : null),
        promoUntil: data.promo_until || null,
      };
    } catch (e) { console.warn('getEntitlement', e); return { pro: false, status: null }; }
  }

  // Ask the server to re-read the store and refresh `entitlements`. Called
  // after a purchase and after "Restore Purchases" so Pro is live immediately
  // rather than whenever the webhook lands.
  //
  // ‼️ ON SUCCESS THIS BROADCASTS `vital:entitlement-changed`. Four screens
  // render differently for a subscriber (the NCHK export sheet's audit-pack
  // toggle, the AI scan gate, the reminder lead-day stepper, Profile), and each
  // used to read the entitlement once on mount with nothing to tell it the
  // answer had changed. A nurse could therefore buy Pro and watch the rest of
  // the app carry on showing her the free tier until it happened to remount —
  // which reads as "my payment failed". paywall.jsx's useProEntitlement()
  // listens for this event; if you add another Pro-aware screen, use that hook
  // rather than a fresh one-shot getEntitlement().
  async function syncEntitlement() {
    try {
      const { data: { session } } = await sb.auth.getSession();
      if (!session?.access_token) return { pro: false, error: { code: 'no_session' } };
      const res = await fetch(`${FUNCTIONS_BASE}/iap-verify`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: '{}',
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) return { pro: false, error: out?.error || { code: 'verify_failed' } };
      const ent = { pro: !!out.pro, status: out.status, expiresAt: out.expiresAt };
      // Notify AFTER the server has written the row, so every listener's
      // re-read of `entitlements` sees the new state. The event carries the
      // result only as a convenience — listeners re-read rather than trust it,
      // because the row is the authority and the client never is.
      try {
        window.dispatchEvent(new CustomEvent('vital:entitlement-changed', { detail: ent }));
      } catch (_) { /* non-browser host — nothing to notify */ }
      return ent;
    } catch (e) {
      console.warn('syncEntitlement', e);
      return { pro: false, error: { code: 'network' } };
    }
  }

  // ── Vital access codes (優惠碼) ───────────────────────────────────────────
  //
  // ‼️ THE CLIENT DECIDES NOTHING HERE. redeem_promo_code() takes no user id —
  // it reads auth.uid() itself — validates the window, the seat count and the
  // per-user limit under a row lock, and writes the entitlement. This function
  // only carries the typed answer back.
  //
  // ‼️ AND IT NEVER THROWS ON A BAD CODE. The RPC returns { ok, reason } rather
  // than raising, so the attempt log survives; a thrown error here would make
  // "wrong code" and "server down" look identical to the caller.
  async function redeemPromoCode(code) {
    try {
      const { data, error } = await sb.rpc('redeem_promo_code', { p_code: String(code || '') });
      if (error) { console.warn('redeemPromoCode', error); return { ok: false, reason: 'network' }; }
      // Postgres returns a one-row set for a TABLE-returning function.
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return { ok: false, reason: 'network' };
      if (row.ok) {
        // Same broadcast a purchase makes: every Pro-aware screen re-reads the
        // server row, so redeeming unlocks the whole app at once instead of at
        // the next remount.
        try {
          window.dispatchEvent(new CustomEvent('vital:entitlement-changed', { detail: { pro: true } }));
        } catch (_) { /* non-browser host */ }
      }
      return { ok: !!row.ok, reason: row.reason, grantedDays: row.granted_days, proUntil: row.pro_until };
    } catch (e) {
      console.warn('redeemPromoCode', e);
      return { ok: false, reason: 'network' };
    }
  }

  // Admin promo management. Every one of these is admin-checked INSIDE the RPC
  // against auth.uid(); a non-admin session gets 42501 and these return an
  // error rather than an empty list, so the UI can say "not permitted" instead
  // of "no codes".
  const adminPromoList  = () => sb.rpc('admin_promo_list');
  const adminPromoStats = () => sb.rpc('admin_promo_stats');
  const adminPromoCreate = ({ code, grantDays, maxRedemptions = null, perUserLimit = 1,
                              startsAt = null, endsAt = null, note = null }) =>
    sb.rpc('admin_promo_create', {
      p_code: code, p_grant_days: grantDays, p_max_redemptions: maxRedemptions,
      p_per_user_limit: perUserLimit, p_starts_at: startsAt, p_ends_at: endsAt, p_note: note,
    });
  const adminPromoSetStatus = (id, status) =>
    sb.rpc('admin_promo_set_status', { p_id: id, p_status: status });
  const adminPromoDelete = (id) => sb.rpc('admin_promo_delete', { p_id: id });

  // ── 公告 / APP NOTICES ─────────────────────────────────────────────────────
  //
  // Operator-authored dashboard banners. See supabase-migration-notices.sql for
  // the security model. Two rules matter on this side of the wire:
  //
  //   1. ‼️ THE COPY IS PLAIN TEXT AND MUST NEVER BE RENDERED AS HTML.
  //      No dangerouslySetInnerHTML, no markdown-to-HTML, not now and not in a
  //      later "rich notices" change. A notice is the one string in this app
  //      that an operator types and every nurse sees, so it is the one string
  //      where an injection is worth someone's time.
  //      scripts/test-notice-contract.mjs fails the build if that appears.
  //   2. Nothing here decides who may see what. get_active_notices() already
  //      resolved the audience server-side; the client renders what it is given.

  // Two shapes only, mirroring app_notices_cta_url_chk in the migration BYTE
  // FOR BYTE (test:notices asserts both sides agree). Re-checked here so a link
  // is validated again at tap time, on the device, after any amount of
  // transport — the CHECK constraint is the authority, this is the seatbelt.
  const NOTICE_CTA_VITAL_RE = /^vital:\/\/[a-z0-9][a-z0-9/_-]*$/i;
  const NOTICE_CTA_HTTPS_RE = /^https:\/\/([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(\/[^\s]*)?$/i;

  function isSafeNoticeCtaUrl(url) {
    if (typeof url !== 'string') return false;
    const u = url.trim();
    if (!u || u.length > 500) return false;
    // Whitespace and control characters first: they are how a payload hides
    // from a regex that only anchors at the ends. Written as \u escapes on
    // purpose — a literal control byte in this file is invisible in review
    // and makes grep treat db.js as a binary file.
    if (/[\s\u0000-\u001f\u007f]/.test(u)) return false;
    return NOTICE_CTA_VITAL_RE.test(u) || NOTICE_CTA_HTTPS_RE.test(u);
  }

  // Guests get no dismissal rows (no user id), so theirs live here. Lost on
  // reinstall and not synced across devices — which is exactly the flaw the
  // server-side table fixes for signed-in nurses, and the reason a
  // guests-targeted notice should always carry an ends_at.
  const NOTICE_GUEST_DISMISS_KEY = 'vital:noticeDismissed';
  function guestDismissedSlugs() {
    try {
      const raw = localStorage.getItem(NOTICE_GUEST_DISMISS_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr.filter(s => typeof s === 'string') : []);
    } catch (_) { return new Set(); }
  }
  function rememberGuestDismissal(slug) {
    try {
      const next = guestDismissedSlugs();
      next.add(slug);
      // Bounded: a guest who dismisses hundreds of notices over a year should
      // not grow this key without limit.
      localStorage.setItem(NOTICE_GUEST_DISMISS_KEY,
        JSON.stringify([...next].slice(-50)));
    } catch (_) { /* private mode — the notice simply returns next launch */ }
  }

  // Everything currently addressed to this caller, newest/most-severe first.
  // Returns [] on any failure: a dashboard that renders without its banners is
  // a far better outcome than one that fails to render.
  async function getActiveNotices() {
    try {
      const { data, error } = await sb.rpc('get_active_notices');
      if (error) { console.warn('getActiveNotices', error); return []; }
      const rows = Array.isArray(data) ? data : [];
      const { data: { session } } = await sb.auth.getSession();
      if (session?.user?.id) return rows;
      const dismissed = guestDismissedSlugs();
      return rows.filter(n => !dismissed.has(n.slug));
    } catch (e) {
      console.warn('getActiveNotices', e);
      return [];
    }
  }

  // Signed-in: a row. Guest: localStorage. Returns true when the caller should
  // stop seeing it. A server refusal (non-dismissible) returns false, and the
  // banner correctly stays put.
  async function dismissNotice(noticeId, slug) {
    try {
      const { data: { session } } = await sb.auth.getSession();
      if (!session?.user?.id) { rememberGuestDismissal(slug); return true; }
      const { data, error } = await sb.rpc('dismiss_notice', { p_notice_id: noticeId });
      if (error) { console.warn('dismissNotice', error); return false; }
      return data === true;
    } catch (e) {
      console.warn('dismissNotice', e);
      return false;
    }
  }

  // Fire-and-forget reach tracking. Never awaited by the UI and never surfaces
  // an error — this is instrumentation, and instrumentation does not get to
  // slow down or break a dashboard.
  function markNoticesSeen(noticeIds) {
    const ids = (noticeIds || []).filter(Boolean).slice(0, 20);
    if (!ids.length) return;
    sb.rpc('mark_notices_seen', { p_notice_ids: ids })
      .then(({ error }) => { if (error) console.warn('markNoticesSeen', error); })
      .catch(e => console.warn('markNoticesSeen', e));
  }

  // Admin. Each is admin-checked INSIDE the RPC against auth.uid() with
  // is_full_admin() — 'admin' only, never 'moderator'. A moderator session gets
  // 42501 and an error, so the UI can say "not permitted" rather than "no
  // notices".
  const adminNoticeList = () => sb.rpc('admin_notice_list');
  const adminNoticeUpsert = ({
    id = null, slug, titleEn, titleZh, bodyEn, bodyZh,
    severity = 'info', audience = 'all',
    ctaLabelEn = null, ctaLabelZh = null, ctaUrl = null,
    startsAt = null, endsAt = null, dismissible = true, priority = 0,
  }) => sb.rpc('admin_notice_upsert', {
    p_id: id, p_slug: slug,
    p_title_en: titleEn, p_title_zh: titleZh,
    p_body_en: bodyEn, p_body_zh: bodyZh,
    p_severity: severity, p_audience: audience,
    p_cta_label_en: ctaLabelEn, p_cta_label_zh: ctaLabelZh, p_cta_url: ctaUrl,
    p_starts_at: startsAt, p_ends_at: endsAt,
    p_dismissible: dismissible, p_priority: priority,
  });
  const adminNoticeSetStatus = (id, status) =>
    sb.rpc('admin_notice_set_status', { p_id: id, p_status: status });
  const adminNoticeDelete = (id) => sb.rpc('admin_notice_delete', { p_id: id });

  // ‼️ THE ONE NOTICE CALL THAT NEEDS AN EDGE FUNCTION. Pushing reaches every
  // nurse's phone and authenticates to broadcast-notice with CRON_SECRET, which
  // the client cannot hold. Everything else above is a direct RPC.
  const adminNoticeBroadcast = (noticeId, { dryRun = false } = {}) =>
    adminCallEdge('admin-notices', {
      action: dryRun ? 'broadcastDryRun' : 'broadcast', noticeId,
    });

  // Balance snapshot for the UI (scan_credit_status RPC is user-callable for
  // the caller's own id). Returns { available, freeRemaining, bonusRemaining,
  // freeResetsOn } or null on any error (UI hides the count).
  async function getScanCreditStatus() {
    try {
      const { data: { session } } = await sb.auth.getSession();
      const uid = session?.user?.id;
      if (!uid) return null;
      const { data, error } = await sb.rpc('scan_credit_status', { p_user_id: uid });
      if (error) { console.warn('getScanCreditStatus', error); return null; }
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return null;
      return {
        available: row.available,
        freeRemaining: row.free_remaining,
        bonusRemaining: row.bonus_remaining,
        freeResetsOn: row.free_resets_on,
      };
    } catch (e) {
      console.warn('getScanCreditStatus', e);
      return null;
    }
  }

  // Share-to-earn: grant the caller a referral credit (server caps at 1/month
  // and self-guards). Call AFTER a completed native share. Returns
  // { granted, available } — granted is 0 when this month's was already claimed.
  // Best-effort: any error resolves to { granted: 0 }.
  async function grantShareCredit() {
    try {
      const { data: { session } } = await sb.auth.getSession();
      const uid = session?.user?.id;
      if (!uid) return { granted: 0, available: null };
      const { data, error } = await sb.rpc('grant_share_credit', { p_user_id: uid });
      if (error) { console.warn('grantShareCredit', error); return { granted: 0, available: null }; }
      const row = Array.isArray(data) ? data[0] : data;
      return { granted: row?.granted || 0, available: row?.available ?? null };
    } catch (e) {
      console.warn('grantShareCredit', e);
      return { granted: 0, available: null };
    }
  }

  // ── Referrals + ward groups (病房裂變引擎) ─────────────────────────────────
  // Canonical origin for every shared link. Kept in ONE place so course-share,
  // referral-share, group-invite and the goal-met hook can never drift onto a
  // different host (universal links only open the app on the exact tapped host).
  const SHARE_ORIGIN = 'https://www.vitalcne.com';
  function buildShareUrl(path, refCode) {
    const p = path && path.startsWith('/') ? path : '/' + (path || '');
    return SHARE_ORIGIN + p + (refCode ? (p.includes('?') ? '&' : '?') + 'ref=' + encodeURIComponent(refCode) : '');
  }

  // The caller's own referral code, created on first call. Cached for the session
  // (the code is immutable server-side). Returns the 6-char code or null.
  let _cachedReferralCode = null;
  async function getMyReferralCode() {
    if (_cachedReferralCode) return _cachedReferralCode;
    try {
      const { data: { session } } = await sb.auth.getSession();
      if (!session?.user?.id) return null;
      const { data, error } = await sb.rpc('get_or_create_referral_code');
      if (error) { console.warn('getMyReferralCode', error); return null; }
      _cachedReferralCode = typeof data === 'string' ? data : null;
      return _cachedReferralCode;
    } catch (e) { console.warn('getMyReferralCode', e); return null; }
  }

  // Redeem a referrer's code at onboarding. Returns { data, error } RAW so the
  // caller can map error.code (P0021 invalid · P0022 own · P0023 too old ·
  // P0024 already redeemed) to a friendly notice. data = { ok, referee_bonus,
  // referrer_capped } on success.
  async function redeemReferralCode(code) {
    const { data, error } = await sb.rpc('redeem_referral_code', { p_code: code });
    if (error) { console.warn('redeemReferralCode', error); return { data: null, error }; }
    return { data, error: null };
  }

  // Ward-group ops. All return { data, error } RAW so the WardGroupScreen can map
  // error.code (P0030 handle required · P0032 not found · P0033 full · P0034 too
  // many groups · P0035 owner must delete). Thin wrappers over SECURITY DEFINER
  // RPCs (no service role; member lists never cross RLS).
  async function createWardGroup(name) {
    const { data, error } = await sb.rpc('create_ward_group', { p_name: name });
    if (error) { console.warn('createWardGroup', error); return { data: null, error }; }
    return { data, error: null };
  }
  async function joinWardGroup(code) {
    const { data, error } = await sb.rpc('join_ward_group', { p_code: code });
    if (error) { console.warn('joinWardGroup', error); return { data: null, error }; }
    return { data, error: null };
  }
  async function leaveWardGroup(groupId) {
    const { data, error } = await sb.rpc('leave_ward_group', { p_group_id: groupId });
    if (error) { console.warn('leaveWardGroup', error); return { data: null, error }; }
    return { data, error: null };
  }
  async function deleteWardGroup(groupId) {
    const { data, error } = await sb.rpc('delete_ward_group', { p_group_id: groupId });
    if (error) { console.warn('deleteWardGroup', error); return { data: null, error }; }
    return { data, error: null };
  }
  async function removeGroupMember(groupId, handle) {
    const { data, error } = await sb.rpc('remove_group_member', { p_group_id: groupId, p_handle: handle });
    if (error) { console.warn('removeGroupMember', error); return { data: null, error }; }
    return { data, error: null };
  }
  async function getMyWardGroups() {
    const { data, error } = await sb.rpc('get_my_groups');
    if (error) { console.warn('getMyWardGroups', error); return { data: null, error }; }
    return { data: data || [], error: null };
  }
  async function getWardGroupProgress(groupId) {
    const { data, error } = await sb.rpc('get_group_progress', { p_group_id: groupId });
    if (error) { console.warn('getWardGroupProgress', error); return { data: null, error }; }
    return { data, error: null };
  }

  // Nudge ("提一提") a ward-group colleague. Goes through the `nudge` edge
  // function (JWT-verified) which validates membership, rate-limits (1/day/pair),
  // writes the in-app notification and relays the push. Returns { ok } or
  // { error: { code } } so the UI can map 429 (already nudged today) etc.
  async function nudgeGroupMember(groupId, handle) {
    try {
      const { data: { session } } = await sb.auth.getSession();
      if (!session?.access_token) return { error: { message: 'Not signed in' } };
      const resp = await fetch(`${FUNCTIONS_BASE}/nudge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ groupId, handle }),
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || !result.ok) {
        return { error: { message: result?.error || `Nudge failed (HTTP ${resp.status})`, code: result?.code, status: resp.status } };
      }
      return { ok: true };
    } catch (e) {
      return { error: { message: e?.message || 'Network error' } };
    }
  }

  // Admin → 增長 dashboard aggregates (referrals / ward groups / nudges).
  // SECURITY DEFINER RPC; assert_admin runs on auth.uid(), so a non-admin gets
  // a 42501 error. Mirrors adminScanStats.
  async function adminGrowthStats() {
    const { data, error } = await sb.rpc('admin_growth_stats');
    if (error) { console.warn('adminGrowthStats', error); return { error }; }
    return { data };
  }

  // Admin → Funnel aggregates. The SECURITY DEFINER RPC performs assert_admin
  // against auth.uid(); this wrapper keeps the UI's failure state non-throwing.
  async function adminOnboardingFunnel() {
    const { data, error } = await sb.rpc('admin_onboarding_funnel');
    if (error) { console.warn('adminOnboardingFunnel', error); return { error }; }
    return { data };
  }

  window.AuthAPI = AuthAPI;
  window.DB = {
    getProfile, upsertProfile, setPrefs,
    getNotificationPrefs, setNotificationPrefs,
    getSessionReminderPrefs, setSessionReminderPrefs,
    getNotifications, getUnreadNotificationCount, markNotificationsRead, deleteNotification,
    markPushSoftAskShown, wasPushSoftAskShown,
    markReviewPrompted, lastReviewPromptedAt,
    markPushOptedOut, clearPushOptedOut, wasPushOptedOut,
    getSaved, toggleSaved,
    getEnrolled, toggleEnrolled,
    getRecords, addRecord, updateRecord, deleteRecord,
    getQualifications, saveQualificationVersion, updateQualificationVersion,
    setQualificationArchived, deleteQualificationVersion, deleteQualification,
    uploadQualificationEvidence,
    getLicenceIdentities,
    getLicenceEvidence,
    uploadLicenceEvidence,
    replaceLicenceEvidence,
    deleteLicenceEvidence,
    getLicenceEvidenceUrl,
    performLicenceRollover,
    rolloverAlreadyCompleted,
    getCourseAttendance, upsertCourseAttendance,
    uploadCertificate, getCertificateUrl, getCertificateDataUrl,
    uploadNoteImage, deleteNoteImage, getNoteImageUrl,
    getReminders, addReminder, dismissReminder,
    fetchCourses, fetchCourseById, fetchProviders,
    // Crowdsource Phase 1A
    SUBMISSION_SCHEMA_VERSION,
    normaliseSubmissionPayload,
    uploadSubmissionEvidence,
    submitCourse,
    // Submission guard (§ submission-guard): one authority for "have I already
    // submitted this?". checkSubmissionCandidate is ADVISORY — the server's
    // verdict on write is what decides.
    computeArtifactDigest,
    checkSubmissionCandidate,
    isPendingEditEnabled,
    editMySubmission,
    withdrawMySubmission,
    isSubmissionDuplicateError,
    reportCourseIssue,
    fetchMySubmissions,
    fetchMyReports,
    fetchMyFeedback,
    fetchMyContributionRewards,
    submitFeedback,
    // Community social (attribution · avatars · reactions)
    getCourseContributors,
    getCourseFieldContributions,
    getCourseReactionSummary,
    toggleCourseReaction,
    fetchExperienceRollups,
    // Course reviews (stars + chips)
    reviewBackendMissing,
    saveCourseRating,
    submitCourseReviewBody,
    withdrawCourseReview,
    fetchCourseReviewsPublic,
    reportCourseReview,
    blockReviewAuthor,
    clearReviewAuthorBlocks,
    submitCourseReview,   // deprecated, see comment at the definition
    fetchMyCourseReview,
    fetchMyCourseReviews,
    fetchMyReviews,
    fetchReviewRollups,
    setCommunityHandle,
    reportInappropriateHandle,
    hasApprovedContribution,
    // Crowdsource Phase 4 (admin UI)
    isAdmin,
    PAGINATION_WARN_THRESHOLD,
    fetchPendingSubmissions,
    fetchDuplicateCandidates,
    fetchPendingReports,
    fetchAcceptedReports,
    fetchAllFeedback,
    updateFeedbackStatus,
    adminReplyToFeedback,
    adminListPendingReviews,
    adminModerateReview,
    adminSuspendReviewAuthor,
    fetchProfileNamesBatch,
    fetchLockedFieldsForCourses,
    fetchContributorStatsBatch,
    fetchContributorHistory,
    getSubmissionEvidenceUrl,
    fetchProviderLabels,
    cachedProviderLabels,
    adminApproveSubmission,
    adminIdentityCandidates,
    adminFindRegistrationLink,
    adminRejectSubmission,
    adminAcceptReport,
    adminRejectReport,
    adminApplyReport,
    // Admin role management
    fetchAdminProfiles,
    searchProfiles,
    setProfileRole,
    // ‼️ EXPORTED SO THE UI CAN ASK BEFORE OFFERING. See the note on the
    // declaration: a control whose handler cannot succeed is the defect this
    // export exists to make impossible.
    submissionIsVerifiable,
    submissionIsShareable,
    courseLastDay,
    hkTodayIso,
    // AI certificate scan
    extractCertificate,
    getScanCreditStatus,
    // Pro entitlement — RENDERING only. The server decides what Pro unlocks.
    getEntitlement, syncEntitlement,
    redeemPromoCode,
    adminPromoList, adminPromoStats, adminPromoCreate, adminPromoSetStatus, adminPromoDelete,
    // 公告 / app notices — dashboard banners
    getActiveNotices, dismissNotice, markNoticesSeen, isSafeNoticeCtaUrl,
    isFullAdmin,
    adminNoticeList, adminNoticeUpsert, adminNoticeSetStatus, adminNoticeDelete,
    adminNoticeBroadcast,
    getScanEarnStatus,
    grantShareCredit,
    logAiScanFeedback,
    adminScanStats,
    // Referrals + ward groups (病房裂變引擎)
    buildShareUrl,
    getMyReferralCode,
    redeemReferralCode,
    createWardGroup,
    joinWardGroup,
    leaveWardGroup,
    deleteWardGroup,
    removeGroupMember,
    getMyWardGroups,
    getWardGroupProgress,
    nudgeGroupMember,
    adminGrowthStats,
    adminOnboardingFunnel,
  };
})();
